import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Dither from '../components/Dither';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDocs, getDoc, setDoc, collection, addDoc } from 'firebase/firestore';
import * as pdfjsLib from 'pdfjs-dist';
import './Isho.css';
import { diffWords } from 'diff';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.mjs`;

// ═══════════════════════════════════════════════════════════
//  CLIENT-SIDE ADAPTIVE CLASSIFIER (mirrors backend)
// ═══════════════════════════════════════════════════════════
const INTENT_PATTERNS = {
    compare: { keywords: ['compare', 'vs', 'versus', 'difference between', 'differences', 'better than', 'which one', 'which is better', 'pros and cons', 'advantages', 'disadvantages', 'tradeoffs', 'trade-offs'], layout: 'comparison_table', label: 'COMPARISON TABLE' },
    learn: { keywords: ['teach me', 'learn', 'how do i', 'tutorial', 'guide me', 'walk me through', 'step by step', 'getting started', 'introduction to', 'intro to', 'basics of', 'fundamentals', 'beginner', 'learning path'], layout: 'roadmap', label: 'LEARNING ROADMAP' },
    decide: { keywords: ['should i', 'recommend', 'suggestion', 'what would you', 'which should', 'best option', 'best choice', 'pick between', 'choose between', 'decision', 'advice', 'worth it'], layout: 'decision_tree', label: 'DECISION TREE' },
    build: { keywords: ['build', 'create', 'implement', 'make a', 'develop', 'code a', 'write a', 'set up', 'setup', 'configure', 'install', 'deploy', 'architecture for', 'design a system'], layout: 'technical_spec', label: 'TECHNICAL SPEC' },
    plan: { keywords: ['plan', 'schedule', 'timeline', 'roadmap', 'milestones', 'sprint', 'project plan', 'phases', 'strategy', 'rollout', 'launch plan'], layout: 'timeline', label: 'TIMELINE' },
    debug: { keywords: ['debug', 'fix', 'error', 'bug', 'issue', 'not working', 'broken', 'crash', 'failing', 'failed', 'wrong', 'problem with', 'troubleshoot', 'diagnose'], layout: 'diagnostic_flow', label: 'DIAGNOSTIC FLOW' },
    research: { keywords: ['research', 'analyze', 'analysis', 'investigate', 'study', 'deep dive', 'report on', 'findings', 'overview of'], layout: 'research_report', label: 'RESEARCH REPORT' },
    explain: { keywords: ['explain', 'what is', 'what are', 'define', 'meaning of', 'how does', 'how do', 'concept', 'tell me about', 'describe', 'elaborate'], layout: 'progressive_disclosure', label: 'EXPLANATION' },
    brainstorm: { keywords: ['brainstorm', 'ideas for', 'suggest', 'creative', 'come up with', 'think of', 'possibilities', 'alternatives', 'options for'], layout: 'brainstorm_board', label: 'BRAINSTORM' },
    checklist: { keywords: ['checklist', 'todo', 'to-do', 'tasks', 'requirements', 'prerequisites', 'what do i need', 'audit'], layout: 'checklist', label: 'CHECKLIST' }
};

function classifyUserMessage(text) {
    if (!text || text.length < 8) return { intent: 'general', layout: 'conversation', label: 'RESPONSE' };
    const lower = text.toLowerCase().trim();
    let detectedIntent = 'general';
    let detectedLayout = 'conversation';
    let detectedLabel = 'RESPONSE';
    let maxScore = 0;

    for (const [intent, config] of Object.entries(INTENT_PATTERNS)) {
        let score = 0;
        for (const kw of config.keywords) {
            if (lower.includes(kw)) {
                const weight = (kw === 'vs' || kw === 'bug' || kw === 'fix' || kw === 'todo') ? 5 : kw.length;
                score += weight;
            }
        }
        if (score > maxScore) {
            maxScore = score;
            detectedIntent = intent;
            detectedLayout = config.layout;
            detectedLabel = config.label;
        }
    }

    // Fallback confidence threshold
    const CONFIDENCE_THRESHOLD = 4;
    if (maxScore < CONFIDENCE_THRESHOLD) {
        return { intent: 'general', layout: 'conversation', label: 'RESPONSE' };
    }

    return { intent: detectedIntent, layout: detectedLayout, label: detectedLabel };
}

const getOrCreateSessionId = () => {
    let sid = sessionStorage.getItem('isho_session_id');
    if (!sid) {
        sid = (typeof crypto !== 'undefined' && crypto.randomUUID) 
            ? crypto.randomUUID() 
            : 'session_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
        sessionStorage.setItem('isho_session_id', sid);
    }
    return sid;
};

const logTelemetry = async (meta, clientLayout, queryLength) => {
    try {
        await addDoc(collection(db, 'isho_telemetry'), {
            id: (typeof crypto !== 'undefined' && crypto.randomUUID) 
                ? crypto.randomUUID() 
                : 'telemetry_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36),
            sessionId: getOrCreateSessionId(),
            timestamp: Date.now(),
            intent: meta.intent || 'general',
            expertise: meta.expertise || 'intermediate',
            clientLayout: clientLayout || 'conversation',
            serverLayout: meta.layout || 'conversation',
            mismatch: (clientLayout || 'conversation') !== (meta.layout || 'conversation'),
            verbosity: meta.verbosity || 'standard',
            queryLength: queryLength || 0,
            tokenBudget: meta.tokenBudget || 1024
        });
    } catch (e) {
        // silent fail — never block the UI for telemetry
        console.warn('[ISHO Telemetry] Silent fail logging telemetry:', e);
    }
};

// ═══════════════════════════════════════════════════════════
//  COLLAPSIBLE SECTIONS & TL;DR PARSING ENGINE
// ═══════════════════════════════════════════════════════════
const TLDR_CHAR_THRESHOLD = 800;

function extractTldr(text) {
    if (!text || text.length < TLDR_CHAR_THRESHOLD) return null;

    // 1. Explicit TL;DR block quote: > **[TL;DR]** or similar
    const explicitTldrRegex = /^>\s*\*\*\[?TL;?DR\]?\*\*[\s\S]*?(?=\n\n|\n[^\s>])/mi;
    const explicitMatch = text.match(explicitTldrRegex);
    if (explicitMatch) {
        return explicitMatch[0]
            .replace(/^>\s*/gm, '') // remove blockquote angle brackets
            .replace(/^\*\*\[?TL;?DR\]?\*\*\s*:\s*/i, '') // remove header label
            .replace(/^\*\*\[?TL;?DR\]?\*\*/i, '')
            .trim();
    }

    // 2. Fallback to first paragraph if total text is long
    const paragraphs = text.split(/\n\n+/);
    if (paragraphs.length > 0) {
        const firstPara = paragraphs[0].trim();
        // Make sure it doesn't contain a header, blockquote, or code block and is reasonably descriptive
        if (!firstPara.startsWith('#') && !firstPara.startsWith('>') && !firstPara.startsWith('`') && firstPara.length > 40) {
            return firstPara;
        }
    }

    return null;
}

function partitionResponseSections(text) {
    if (!text) return { intro: '', sections: [] };

    const h2Pattern = /^##\s+(.+)$/gm;
    const sections = [];
    const headings = [];
    let match;

    h2Pattern.lastIndex = 0;
    while ((match = h2Pattern.exec(text)) !== null) {
        headings.push({
            title: match[1].trim(),
            index: match.index,
            length: match[0].length
        });
    }

    if (headings.length === 0) {
        return { intro: text, sections: [] };
    }

    const intro = text.substring(0, headings[0].index).trim();

    for (let i = 0; i < headings.length; i++) {
        const start = headings[i].index + headings[i].length;
        const end = (i + 1 < headings.length) ? headings[i + 1].index : text.length;
        sections.push({
            title: headings[i].title,
            content: text.substring(start, end).trim()
        });
    }

    return { intro, sections };
}

function parseQuiz(text) {
    if (!text) return null;
    const startIdx = text.indexOf('[QUIZ_START]');
    const endIdx = text.indexOf('[QUIZ_END]');
    if (startIdx === -1 || endIdx === -1) return null;

    const quizContent = text.substring(startIdx + 12, endIdx).trim();
    const lessonText = text.substring(0, startIdx).trim();

    const lines = quizContent.split('\n');
    let question = '';
    const options = [];

    for (let line of lines) {
        line = line.trim();
        if (!line) continue;
        
        const optionMatch = line.match(/^\[([A-E])\]\s*(.*)/i) || line.match(/^([A-E])[\s).:-]+\s*(.*)/i);
        if (optionMatch) {
            options.push({
                key: optionMatch[1].toUpperCase(),
                text: optionMatch[2].trim(),
                raw: line
            });
        } else {
            if (question) {
                question += '\n' + line;
            } else {
                question = line;
            }
        }
    }

    return {
        lessonText,
        question: question.trim(),
        options
    };
}

function parseQuizV2(text) {
    if (!text) return null;
    const startIdx = text.indexOf('[QUIZ_START]');
    const endIdx = text.indexOf('[QUIZ_END]');
    if (startIdx === -1 || endIdx === -1) return null;

    const quizContent = text.substring(startIdx + 12, endIdx).trim();
    const lessonText = text.substring(0, startIdx).trim();

    const typeMatch = quizContent.match(/\[TYPE\]\s*(\w+)/i);
    const type = typeMatch ? typeMatch[1].trim().toLowerCase() : 'multiple_choice';

    const questionMatch = quizContent.match(/\[QUESTION\]\s*([^[\]]+)/i) || quizContent.match(/\[QUESTION\]\s*([\s\S]*?)(?=\[[A-Z]+\]|$)/i);
    const question = questionMatch ? questionMatch[1].trim() : '';

    const expectedMatch = quizContent.match(/\[EXPECTED\]\s*([^[\]]+)/i) || quizContent.match(/\[EXPECTED\]\s*([\s\S]*?)(?=\[[A-Z]+\]|$)/i);
    const expected = expectedMatch ? expectedMatch[1].trim() : '';

    const answerMatch = quizContent.match(/\[ANSWER\]\s*([A-E])/i);
    const answer = answerMatch ? answerMatch[1].trim().toUpperCase() : '';

    // Options parsing
    const lines = quizContent.split('\n');
    const options = [];
    for (let line of lines) {
        line = line.trim();
        if (!line) continue;
        
        const optionMatch = line.match(/^\[([A-E])\]\s*(.*)/i) || line.match(/^([A-E])[\s).:-]+\s*(.*)/i);
        if (optionMatch) {
            options.push({
                key: optionMatch[1].toUpperCase(),
                text: optionMatch[2].trim(),
                raw: line
            });
        }
    }

    return {
        lessonText,
        type,
        question,
        options,
        answer,
        expected
    };
}

function calculateSM2(quality, prevInterval = 0, prevEaseFactor = 2.5, prevRepetitions = 0) {
    let easeFactor = prevEaseFactor;
    let repetitions = prevRepetitions;
    let interval = 0;

    easeFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    if (easeFactor < 1.3) {
        easeFactor = 1.3;
    }

    if (quality >= 3) {
        if (repetitions === 0) {
            interval = 1;
        } else if (repetitions === 1) {
            interval = 6;
        } else {
            interval = Math.round(prevInterval * easeFactor);
        }
        repetitions += 1;
    } else {
        repetitions = 0;
        interval = 1;
    }

    const dueDate = Date.now() + interval * 24 * 60 * 60 * 1000;

    return {
        interval,
        easeFactor,
        repetitions,
        dueDate
    };
}

function stripScoreChange(text) {
    if (!text) return { text: '', scoreChange: null };
    const match = text.match(/^\[SCORE_CHANGE\]\s*([+-]?\d+)\s*\n?/);
    if (match) {
        const val = parseInt(match[1], 10);
        return {
            text: text.substring(match[0].length).trim(),
            scoreChange: val
        };
    }
    return { text, scoreChange: null };
}

function parseChips(text) {
    if (!text) return null;
    const startIdx = text.indexOf('[CHIPS]');
    const endIdx = text.indexOf('[/CHIPS]');
    if (startIdx === -1 || endIdx === -1) return null;
    const chipsContent = text.substring(startIdx + 7, endIdx).trim();
    return chipsContent.split('|').map(c => c.trim()).filter(Boolean);
}

function parseConfidence(text) {
    if (!text) return null;
    const match = text.match(/\[CONFIDENCE:(\d+)\]/);
    return match ? parseInt(match[1], 10) : null;
}

const CURRICULUM_NODES = [
    // Web Development Track
    { id: 'git', label: 'Git Basics', x: 80, y: 70, next: ['docker', 'html_css'] },
    { id: 'html_css', label: 'HTML & CSS', x: 220, y: 70, next: ['javascript'] },
    { id: 'javascript', label: 'Javascript', x: 360, y: 70, next: ['react'] },
    
    // Devops/Systems Track
    { id: 'docker', label: 'Docker', x: 80, y: 190, next: ['kubernetes'] },
    { id: 'kubernetes', label: 'Kubernetes', x: 220, y: 190, next: ['dist_sys'] },
    { id: 'dist_sys', label: 'Distributed Systems', x: 360, y: 190, next: [] },
    
    // Frameworks Track
    { id: 'react', label: 'React.js', x: 360, y: 310, next: ['next_js'] },
    { id: 'next_js', label: 'Next.js Spec', x: 220, y: 310, next: ['dist_sys'] }
];

const parseSummary = (text) => {
    if (!text) return [];
    return text
        .split('●')
        .map(t => t.trim())
        .filter(t => t.length > 0);
};

export default function Isho() {
    const { currentUser } = useAuth();
    const [messages, setMessages] = useState([{
        id: 0,
        role: 'assistant',
        content: "HELLO. THIS IS ISHO.\n\nSYSTEMS ARE ONLINE. I CAN PROCESS TEXT, ANALYZE IMAGES, AND READ PDFS. HOW CAN I ASSIST YOU TODAY?",
        parentId: null
    }]);
    const [input, setInput] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [replyToMsgId, setReplyToMsgId] = useState(null);
    const [showSplitModal, setShowSplitModal] = useState(false);
    const [perspectiveA, setPerspectiveA] = useState('Senior Engineer');
    const [perspectiveB, setPerspectiveB] = useState('Product Manager');
    const [isLoading, setIsLoading] = useState(false);
    const [activeChips, setActiveChips] = useState([]);
    const [isSummarizing, setIsSummarizing] = useState(false);

    // Blinking cursor state for streaming text
    const [showCursor, setShowCursor] = useState(false);
    useEffect(() => {
        if (!isLoading) {
            setShowCursor(false);
            return;
        }
        setShowCursor(true);
        const interval = setInterval(() => {
            setShowCursor(prev => !prev);
        }, 500);
        return () => clearInterval(interval);
    }, [isLoading]);
    
    // Multi-chat State Tracking
    const [chatHistory, setChatHistory] = useState([]);
    const [currentChatId, setCurrentChatId] = useState(Date.now().toString());
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const [isLearnMode, setIsLearnMode] = useState(false);
    const [isLearnDrawerOpen, setIsLearnDrawerOpen] = useState(true);
    const [learnTab, setLearnTab] = useState('focus'); // focus, path, stats
    const [learnProgress, setLearnProgress] = useState(null);
    const [activeQuiz, setActiveQuiz] = useState(null);
    const [openEndedAnswer, setOpenEndedAnswer] = useState('');
    const [difficulty, setDifficulty] = useState(() => localStorage.getItem('isho_difficulty') || 'adult');
    const [stats, setStats] = useState({
        streak_count: 0,
        last_active_date: null,
        unlocked_achievements: []
    });
    const [allTopicsProgress, setAllTopicsProgress] = useState({});

    const [guestQueryCount, setGuestQueryCount] = useState(() => {
        const stored = localStorage.getItem('isho_guest_query_count_v1');
        return stored ? parseInt(stored, 10) : 0;
    });
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [isFocusMode, setIsFocusMode] = useState(false);
    const activeChatObj = chatHistory.find(c => c.id === currentChatId);
    const [sidebarTab, setSidebarTab] = useState('sessions'); // sessions or bookmarks
    const [activeBookmarkInputMsgId, setActiveBookmarkInputMsgId] = useState(null);
    const [bookmarkLabel, setBookmarkLabel] = useState('');
    const messageRefs = useRef({});

    // Sync isFocusMode state to document.body class and close side panels
    useEffect(() => {
        if (isFocusMode) {
            document.body.classList.add('isho-focus-mode-active');
            setIsSidebarOpen(false);
            setIsLearnMode(false);
        } else {
            document.body.classList.remove('isho-focus-mode-active');
        }
        return () => {
            document.body.classList.remove('isho-focus-mode-active');
        };
    }, [isFocusMode]);

    // Global keyboard listener for Alt+F to toggle Focus Mode
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.altKey && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                setIsFocusMode(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    // Auto-trigger auth modal for guests who exceeded queries
    useEffect(() => {
        if (!currentUser && guestQueryCount >= 7) {
            setShowAuthModal(true);
        } else {
            setShowAuthModal(false);
        }
    }, [currentUser, guestQueryCount]);

    const normalizeTopicId = useCallback((topic) => {
        return (topic || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    }, []);

    const loadLearnProgress = useCallback(async (topicId) => {
        if (!currentUser) return;
        try {
            const progressRef = doc(db, 'users', currentUser.uid, 'learn_progress', topicId);
            const snap = await getDoc(progressRef);
            if (snap.exists()) {
                const data = snap.data();
                setLearnProgress(data);
                if (data.active_quiz_type) {
                    setActiveQuiz({
                        type: data.active_quiz_type,
                        question: data.last_question,
                        options: data.active_quiz_options || [],
                        answer: data.correct_answer_key || '',
                        expected: data.active_quiz_expected || ''
                    });
                } else {
                    setActiveQuiz(null);
                }
            } else {
                setLearnProgress(null);
                setActiveQuiz(null);
            }
        } catch (e) {
            console.error("Failed to load learn progress", e);
        }
    }, [currentUser]);

    const loadAllTopicsProgress = useCallback(async () => {
        if (!currentUser) return;
        try {
            const collRef = collection(db, 'users', currentUser.uid, 'learn_progress');
            const snap = await getDocs(collRef);
            const progressObj = {};
            snap.forEach(doc => {
                progressObj[doc.id] = doc.data();
            });
            setAllTopicsProgress(progressObj);
        } catch (e) {
            console.error("Failed to load all topics progress", e);
        }
    }, [currentUser]);

    const loadStats = useCallback(async () => {
        if (!currentUser) return;
        try {
            const statsRef = doc(db, 'users', currentUser.uid, 'learn_metadata', 'stats');
            const snap = await getDoc(statsRef);
            if (snap.exists()) {
                setStats(snap.data());
            } else {
                const initialStats = {
                    streak_count: 0,
                    last_active_date: null,
                    unlocked_achievements: []
                };
                await setDoc(statsRef, initialStats);
                setStats(initialStats);
            }
        } catch (e) {
            console.error("Failed to load learning stats", e);
        }
    }, [currentUser]);

    const checkAndIncrementStreakOnLoad = useCallback(async () => {
        if (!currentUser) return;
        try {
            const statsRef = doc(db, 'users', currentUser.uid, 'learn_metadata', 'stats');
            const snap = await getDoc(statsRef);
            let currentStats = snap.exists() ? snap.data() : { streak_count: 0, last_active_date: null, unlocked_achievements: [] };

            const todayStr = new Date().toISOString().split('T')[0];
            let newStreak = currentStats.streak_count || 0;

            if (currentStats.last_active_date) {
                if (currentStats.last_active_date !== todayStr) {
                    const lastActive = new Date(currentStats.last_active_date);
                    const today = new Date(todayStr);
                    const diffTime = Math.abs(today - lastActive);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays === 1) {
                        newStreak += 1;
                    } else if (diffDays > 1) {
                        newStreak = 1;
                    }

                    const updated = {
                        ...currentStats,
                        streak_count: newStreak,
                        last_active_date: todayStr
                    };
                    await setDoc(statsRef, updated, { merge: true });
                    setStats(updated);
                } else {
                    setStats(currentStats);
                }
            } else {
                newStreak = 1;
                const updated = {
                    streak_count: newStreak,
                    last_active_date: todayStr,
                    unlocked_achievements: currentStats.unlocked_achievements || []
                };
                await setDoc(statsRef, updated, { merge: true });
                setStats(updated);
            }
        } catch (e) {
            console.error("Failed to check/increment streak on load", e);
        }
    }, [currentUser]);

    const updateStreakAndStats = useCallback(async (isCorrect, questionType, topicId, finalMastery) => {
        if (!currentUser) return;
        try {
            const statsRef = doc(db, 'users', currentUser.uid, 'learn_metadata', 'stats');
            const snap = await getDoc(statsRef);
            let currentStats = snap.exists() ? snap.data() : { streak_count: 0, last_active_date: null, unlocked_achievements: [] };

            const todayStr = new Date().toISOString().split('T')[0];
            let newStreak = currentStats.streak_count || 0;

            if (currentStats.last_active_date) {
                const lastActive = new Date(currentStats.last_active_date);
                const today = new Date(todayStr);
                const diffTime = Math.abs(today - lastActive);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 1) {
                    newStreak += 1;
                } else if (diffDays > 1) {
                    newStreak = 1;
                }
            } else {
                newStreak = 1;
            }

            const achievements = [...(currentStats.unlocked_achievements || [])];
            const unlock = (id) => {
                if (!achievements.includes(id)) {
                    achievements.push(id);
                    console.log(`🏆 ACHIEVEMENT UNLOCKED: ${id}`);
                }
            };

            unlock('socratic_rookie');
            if (isCorrect) {
                if (questionType === 'analogy') unlock('analogy_master');
                if (questionType === 'debate') unlock('debate_gladiator');
                if (questionType === 'teach_back') unlock('master_educator');
                if (questionType === 'scenario') unlock('crisis_solver');
            }
            if (newStreak >= 7) unlock('seven_day_streak');
            if (finalMastery >= 100) unlock('topic_conqueror');

            const updated = {
                streak_count: newStreak,
                last_active_date: todayStr,
                unlocked_achievements: achievements
            };

            await setDoc(statsRef, updated, { merge: true });
            setStats(updated);
        } catch (e) {
            console.error("Failed to update streak/stats", e);
        }
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) {
            loadStats();
            loadAllTopicsProgress();
            checkAndIncrementStreakOnLoad();
        }
    }, [currentUser, loadStats, loadAllTopicsProgress, checkAndIncrementStreakOnLoad]);
    const [usageCount, setUsageCount] = useState(() => {
        const today = new Date().toDateString();
        const stored = localStorage.getItem('isho_usage_v1');
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.date === today) return parsed.count;
        }
        return 0;
    });

    // Auto-scroll container
    const chatContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const scrollAnchorRef = useRef(null);
    const textareaRef = useRef(null);
    const isAutoScrollRef = useRef(true);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [expandedSections, setExpandedSections] = useState({}); // { [msgId]: { [secIdx]: boolean } }

    // Auto-grow textarea height to fit content, up to 200px
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        }
    }, [input]);

    // PDF Vectorization State
    const [pdfStatus, setPdfStatus] = useState('IDLE'); // IDLE, EXTRACTING, CHUNKING, READY
    const pdfChunksRef = useRef(null); // Persistent ref so chunks survive across renders
    const queuedMessagesRef = useRef([]); // Messages waiting for PDF vectorization
    const [isDragging, setIsDragging] = useState(false);

    // Initial Load & Collection Fetch
    useEffect(() => {
        const initChat = () => {
            const newId = Date.now().toString();
            setCurrentChatId(newId);
            setMessages([{
                id: 0,
                role: 'assistant',
                content: "HELLO. THIS IS ISHO.\n\nSYSTEMS ARE ONLINE. I CAN PROCESS TEXT, ANALYZE IMAGES, AND READ PDFS. HOW CAN I ASSIST YOU TODAY?",
                parentId: null
            }]);
        };

        if (currentUser) {
            const fetchLogs = async () => {
                try {
                    const q = collection(db, 'users', currentUser.uid, 'isho_chats');
                    const snap = await getDocs(q);
                    
                    let history = [];
                    snap.forEach(d => {
                        history.push(d.data());
                    });
                    
                    history.sort((a,b) => b.updatedAt - a.updatedAt);
                    setChatHistory(history);

                    if (history.length > 0) {
                        setCurrentChatId(history[0].id);
                        setMessages(normalizeMessages(history[0].messages));
                    } else {
                        initChat();
                    }
                } catch(e) { console.error("Log fetch failed", e); }
            };
            fetchLogs();
        } else {
            initChat();
        }
    }, [currentUser]);

    const startNewChat = useCallback((keepLearnMode = false) => {
        if (isLoading) return;
        const newId = Date.now().toString();
        setCurrentChatId(newId);
        setMessages([{
            id: 0,
            role: 'assistant',
            content: "HELLO. THIS IS ISHO.\n\nSYSTEMS ARE ONLINE. I CAN PROCESS TEXT, ANALYZE IMAGES, AND READ PDFS. HOW CAN I ASSIST YOU TODAY?",
            parentId: null
        }]);
        // Clear PDF and Learn context for new chat
        pdfChunksRef.current = null;
        setPdfStatus('IDLE');
        setActiveChips([]);
        setSidebarTab('sessions');
        if (!keepLearnMode) {
            setIsLearnMode(false);
            setLearnProgress(null);
            setActiveQuiz(null);
        }
        if (window.innerWidth < 768) setIsSidebarOpen(false);
    }, [isLoading]);

    const loadChat = useCallback((chatData) => {
        if (isLoading) return;
        setCurrentChatId(chatData.id);
        setMessages(normalizeMessages(chatData.messages));
        // Clear PDF context when switching chats
        pdfChunksRef.current = null;
        setPdfStatus('IDLE');
        setActiveChips([]);
        setSidebarTab('sessions');
        if (chatData.isLearnMode && chatData.learnTopicId) {
            setIsLearnMode(true);
            loadLearnProgress(chatData.learnTopicId);
        } else {
            setIsLearnMode(false);
            setLearnProgress(null);
            setActiveQuiz(null);
        }
        if (window.innerWidth < 768) setIsSidebarOpen(false);
    }, [isLoading, loadLearnProgress]);




    // Normalize messages from Firestore that predate the parentId field
    const normalizeMessages = (msgs) => {
        if (!Array.isArray(msgs)) return msgs;
        return msgs.map(m => ({
            ...m,
            parentId: m.parentId !== undefined ? m.parentId : null
        }));
    };

    const syncToCloud = useCallback(async (msgsArray, chatIdToSave, customLearnProgress = null, customSummary = null, customBookmarks = null) => {
        if (!currentUser) return;
        try {
            const docRef = doc(db, 'users', currentUser.uid, 'isho_chats', chatIdToSave);
            
            const userMsgs = msgsArray.filter(m => m.role === 'user');
            let generatedTitle = 'NEW CHAT';
            if (userMsgs.length > 0) {
                const firstUser = userMsgs[0];
                if (firstUser.content && firstUser.content.trim() !== '') {
                    generatedTitle = firstUser.content.substring(0, 32).trim() + (firstUser.content.length > 32 ? '...' : '');
                } else if (firstUser.attachment) {
                    generatedTitle = `[${firstUser.attachment.type.toUpperCase()} ATTACHMENT]`;
                }
            }

            const lp = customLearnProgress !== null ? customLearnProgress : learnProgress;

            let summaryToSave = customSummary;
            if (summaryToSave === null) {
                const existingChat = chatHistory.find(c => c.id === chatIdToSave);
                if (existingChat && existingChat.summary) {
                    summaryToSave = existingChat.summary;
                }
            }

            let bookmarksToSave = customBookmarks;
            if (bookmarksToSave === null) {
                const existingChat = chatHistory.find(c => c.id === chatIdToSave);
                if (existingChat && existingChat.bookmarks) {
                    bookmarksToSave = existingChat.bookmarks;
                }
            }

            const payload = {
                id: chatIdToSave,
                title: generatedTitle,
                updatedAt: Date.now(),
                messages: msgsArray,
                isLearnMode: isLearnMode,
                learnTopicId: lp ? normalizeTopicId(lp.topic) : null
            };

            if (summaryToSave) {
                payload.summary = summaryToSave;
            }
            if (bookmarksToSave) {
                payload.bookmarks = bookmarksToSave;
            }

            await setDoc(docRef, payload, { merge: true });

            setChatHistory(prev => {
                const arr = [...prev];
                const idx = arr.findIndex(c => c.id === chatIdToSave);
                if (idx >= 0) arr[idx] = payload;
                else arr.unshift(payload);
                return arr.sort((a,b) => b.updatedAt - a.updatedAt);
            });
        } catch(e) { console.error("Cloud sync failed", e); }
    }, [currentUser, chatHistory, isLearnMode, learnProgress, normalizeTopicId]);

    const triggerSummarization = useCallback(async (msgsArray, chatIdToSave) => {
        if (!currentUser) return;
        setIsSummarizing(true);
        try {
            const idToken = await currentUser.getIdToken();
            const response = await fetch('/api/summarize', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ messages: msgsArray })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.summary) {
                    await syncToCloud(msgsArray, chatIdToSave, null, data.summary);
                }
            }
        } catch (e) {
            console.error("Failed to generate summary", e);
        } finally {
            setIsSummarizing(false);
        }
    }, [currentUser, syncToCloud]);

    const scrollToMessage = useCallback((messageId) => {
        const el = messageRefs.current[messageId];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('isho-message-highlight');
            setTimeout(() => {
                el.classList.remove('isho-message-highlight');
            }, 2000);
        }
    }, []);

    const saveBookmark = useCallback(async (messageId, messageContent) => {
        if (!bookmarkLabel.trim()) return;
        const activeChat = chatHistory.find(c => c.id === currentChatId);
        if (!activeChat) return;

        const messagePreview = messageContent.substring(0, 60).trim() + (messageContent.length > 60 ? '...' : '');
        
        const newBookmark = {
            id: Date.now().toString(),
            messageId,
            label: bookmarkLabel.trim(),
            timestamp: Date.now(),
            messagePreview
        };

        let updatedBookmarks = activeChat.bookmarks ? [...activeChat.bookmarks] : [];
        const existingIdx = updatedBookmarks.findIndex(b => b.messageId === messageId);
        if (existingIdx >= 0) {
            updatedBookmarks[existingIdx] = { ...updatedBookmarks[existingIdx], label: bookmarkLabel.trim() };
        } else {
            updatedBookmarks.push(newBookmark);
        }

        await syncToCloud(messages, currentChatId, null, null, updatedBookmarks);
        setActiveBookmarkInputMsgId(null);
        setBookmarkLabel('');
    }, [chatHistory, currentChatId, messages, bookmarkLabel, syncToCloud]);

    const deleteBookmark = useCallback(async (bookmarkId) => {
        const activeChat = chatHistory.find(c => c.id === currentChatId);
        if (!activeChat || !activeChat.bookmarks) return;

        const updatedBookmarks = activeChat.bookmarks.filter(b => b.id !== bookmarkId);

        await syncToCloud(messages, currentChatId, null, null, updatedBookmarks);
    }, [chatHistory, currentChatId, messages, syncToCloud]);

    const handleScroll = () => {
        if (!chatContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        // User is near bottom if they are within 100px from the bottom
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
        
        isAutoScrollRef.current = isAtBottom;
        setShowScrollButton(!isAtBottom);
    };

    // Scroll when messages list grows or updates
    useEffect(() => {
        if (chatContainerRef.current && isAutoScrollRef.current) {
            const lastMsg = messages[messages.length - 1];
            
            // Smooth scroll for user inputs, instant scroll for streaming tokens
            const behavior = (lastMsg && lastMsg.role === 'user') ? 'smooth' : 'auto';
            
            scrollAnchorRef.current?.scrollIntoView({ behavior });
        }
    }, [messages]);

    // Scroll when attachment state is populated
    useEffect(() => {
        if (chatContainerRef.current && isAutoScrollRef.current && attachment) {
            scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [attachment]);

    const fetchSplitAssistantResponse = useCallback(async (activeMessages, tempId, model, userText, perspA, perspB) => {
        const clientClassification = classifyUserMessage(userText);

        setMessages(prev => {
            const exists = prev.some(m => m.id === tempId);
            if (!exists) {
                return [...prev, { 
                    id: tempId, role: 'assistant', isSplit: true, 
                    perspectiveA: perspA, perspectiveB: perspB,
                    contentA: '', contentB: '',
                    meta: clientClassification, parentId: null 
                }];
            }
            return prev;
        });

        const runStream = async (persp, key) => {
            const apiMessages = activeMessages.filter(m => m.id !== 0).map(m => ({ role: m.role, content: m.content }));
            
            // Inject perspective into the prompt
            apiMessages[apiMessages.length - 1].content = `[Answer from the perspective of a ${persp}]\n\n${userText}`;

            let streamingString = '';
            
            const headers = { 'Content-Type': 'application/json' };
            if (currentUser) {
                try {
                    const idToken = await currentUser.getIdToken();
                    headers['Authorization'] = `Bearer ${idToken}`;
                } catch (e) {}
            }

            try {
                const response = await fetch('/api/isho', {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({ model: model, messages: apiMessages, stream: true })
                });

                if (!response.ok) throw new Error("API Connection Error");

                const reader = response.body.getReader();
                const decoder = new TextDecoder("utf-8");

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    const lines = chunk.split('\n');

                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed) continue;
                        if (trimmed.startsWith('data: ')) {
                            const jsonStr = trimmed.substring(6);
                            if (jsonStr === '[DONE]') break;
                            try {
                                const parsed = JSON.parse(jsonStr);
                                if (parsed.isho_meta) continue;
                                const text = parsed.choices[0]?.delta?.content || "";
                                streamingString += text;
                            } catch(e) {}
                        }
                    }
                    const currentStreamText = streamingString;
                    setMessages(prev => prev.map(m => m.id === tempId ? { ...m, [key]: currentStreamText } : m));
                }
            } catch (error) {
                console.error(`Split Stream Error (${key}):`, error);
                setMessages(prev => prev.map(m => m.id === tempId ? { ...m, [key]: "ERROR: CONNECTION FAILED." } : m));
            }
            return streamingString;
        };

        setIsLoading(true);
        try {
            const [finalA, finalB] = await Promise.all([
                runStream(perspA, 'contentA'),
                runStream(perspB, 'contentB')
            ]);
            
            if (currentUser) {
                const assistantMsg = { 
                    id: tempId, role: 'assistant', isSplit: true,
                    perspectiveA: perspA, perspectiveB: perspB,
                    contentA: finalA, contentB: finalB, parentId: null 
                };
                const updatedMsgs = [...activeMessages, assistantMsg];
                syncToCloud(updatedMsgs, currentChatId);
            }
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, currentChatId, syncToCloud]);

    const fetchAssistantResponse = useCallback(async (activeMessages, tempId, model, userText, currentAttachment, customLP = null, previousContent = null) => {
        let loggedTelemetry = false;
        const today = new Date().toDateString();
        const stored = localStorage.getItem('isho_usage_v1');
        let currentCount = 0;
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.date === today) currentCount = parsed.count;
        }

        if (currentCount >= 20) {
            const limitMsg = {
                id: Date.now(),
                role: 'assistant',
                content: `### ACCESS DENIED: DAILY CYCLES EXCEEDED\n\nYou have utilized all **20 of 20 daily free-tier cycles** for the ISHO AI system (Groq).\n\nPlease try again tomorrow or contact Abraham at abraham@moratadeo.com.`,
                parentId: null
            };
            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== tempId);
                return [...filtered, limitMsg];
            });
            setIsLoading(false);
            return;
        }

        const apiMessages = activeMessages.filter(m => m.id !== 0).map(m => {
            return { role: m.role, content: m.content };
        });

        let apiContent = userText;
        let documentContext = null;

        if (currentAttachment) {
            if (currentAttachment.type === 'image') {
                apiContent = [
                    { type: 'text', text: userText || 'Describe this image.' },
                    { type: 'image_url', image_url: { url: currentAttachment.base64 } }
                ];
            }
        }

        // ── FRONTEND: Prepare Document Context ──
        if (pdfChunksRef.current && pdfChunksRef.current.length > 0) {
            documentContext = pdfChunksRef.current;
        }

        // Replace the last message's content with the formatted apiContent (for images)
        apiMessages[apiMessages.length - 1].content = apiContent;

        // ═══ ADAPTIVE: Client-side classification for immediate layout badge ═══
        const clientClassification = classifyUserMessage(userText);

        // Ensure temp assistant message exists in UI (might be "PREPARING CONTEXT..." if it was queued)
        setMessages(prev => {
            const exists = prev.some(m => m.id === tempId);
            if (exists) {
                return prev.map(m => m.id === tempId ? { ...m, content: '', meta: clientClassification } : m);
            } else {
                return [...prev, { id: tempId, role: 'assistant', content: '', meta: clientClassification, parentId: null }];
            }
        });

        let streamingString = '';

        try {
            const activeLP = customLP || learnProgress;
            const requestBody = {
                model: model,
                messages: apiMessages,
                stream: true,
                is_learn_mode: isLearnMode,
                learn_progress: isLearnMode ? activeLP : null,
                difficulty_level: difficulty
            };

            if (documentContext) {
                requestBody.document_context = documentContext;
            }

            const headers = {
                'Content-Type': 'application/json'
            };

            if (currentUser) {
                try {
                    const idToken = await currentUser.getIdToken();
                    headers['Authorization'] = `Bearer ${idToken}`;
                } catch (e) {
                    console.error("Failed to get client auth token", e);
                }
            }

            const response = await fetch('/api/isho', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) throw new Error("API Connection Error");

            // Increment usage count
            const newCount = currentCount + 1;
            localStorage.setItem('isho_usage_v1', JSON.stringify({ date: today, count: newCount }));
            setUsageCount(newCount);

            // Increment guest query count
            if (!currentUser) {
                setGuestQueryCount(prev => {
                    const nextGuestCount = prev + 1;
                    localStorage.setItem('isho_guest_query_count_v1', nextGuestCount.toString());
                    return nextGuestCount;
                });
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;

                    if (trimmed.startsWith('data: ')) {
                        const jsonStr = trimmed.substring(6);
                        if (jsonStr === '[DONE]') break;
                        try {
                            const parsed = JSON.parse(jsonStr);
                            // ═══ ADAPTIVE: Capture isho_meta from first SSE event ═══
                            if (parsed.isho_meta) {
                                if (parsed.isho_meta.layout !== clientClassification.layout) {
                                    console.warn('[ISHO] Classifier mismatch:', {
                                        client: clientClassification.layout,
                                        server: parsed.isho_meta.layout,
                                        query: userText
                                    });
                                }
                                setMessages(prev => prev.map(m => m.id === tempId ? { ...m, meta: parsed.isho_meta } : m));
                                
                                if (!loggedTelemetry) {
                                    loggedTelemetry = true;
                                    logTelemetry(parsed.isho_meta, clientClassification.layout, userText.length);
                                }
                                continue;
                            }
                            const text = parsed.choices[0]?.delta?.content || "";
                            streamingString += text;
                            
                            const { text: cleanContent } = stripScoreChange(streamingString);
                            let displayContent = cleanContent;
                            const quizIdx = displayContent.indexOf('[QUIZ_START]');
                            if (quizIdx !== -1) {
                                displayContent = displayContent.substring(0, quizIdx).trim();
                            }
                            const chipsIdx = displayContent.indexOf('[CHIPS]');
                            if (chipsIdx !== -1) {
                                displayContent = displayContent.substring(0, chipsIdx).trim();
                            }
                            const confIdx = displayContent.indexOf('[CONFIDENCE:');
                            if (confIdx !== -1) {
                                displayContent = displayContent.substring(0, confIdx).trim();
                            }
                            const score = parseConfidence(streamingString);
                            setMessages(prev => prev.map(m => m.id === tempId ? { ...m, content: displayContent, confidence: score } : m));
                        } catch (e) {}
                    } else if (trimmed.startsWith('{')) {
                        try {
                            const parsed = JSON.parse(trimmed);
                            // ═══ ADAPTIVE: Capture isho_meta from non-SSE JSON ═══
                            if (parsed.isho_meta) {
                                if (parsed.isho_meta.layout !== clientClassification.layout) {
                                    console.warn('[ISHO] Classifier mismatch:', {
                                        client: clientClassification.layout,
                                        server: parsed.isho_meta.layout,
                                        query: userText
                                    });
                                }
                                setMessages(prev => prev.map(m => m.id === tempId ? { ...m, meta: parsed.isho_meta } : m));

                                if (!loggedTelemetry) {
                                    loggedTelemetry = true;
                                    logTelemetry(parsed.isho_meta, clientClassification.layout, userText.length);
                                }
                                continue;
                            }
                            const text = parsed.choices[0]?.delta?.content || (parsed.choices[0]?.message?.content) || "";
                            streamingString += text;
                            
                            const { text: cleanContent } = stripScoreChange(streamingString);
                            let displayContent = cleanContent;
                            const quizIdx = displayContent.indexOf('[QUIZ_START]');
                            if (quizIdx !== -1) {
                                displayContent = displayContent.substring(0, quizIdx).trim();
                            }
                            const chipsIdx = displayContent.indexOf('[CHIPS]');
                            if (chipsIdx !== -1) {
                                displayContent = displayContent.substring(0, chipsIdx).trim();
                            }
                            const confIdx = displayContent.indexOf('[CONFIDENCE:');
                            if (confIdx !== -1) {
                                displayContent = displayContent.substring(0, confIdx).trim();
                            }
                            const score = parseConfidence(streamingString);
                            setMessages(prev => prev.map(m => m.id === tempId ? { ...m, content: displayContent, confidence: score } : m));
                        } catch (e) {}
                    }
                }
            }

            // Extract quiz answers and details if present in learn mode
            const { text: cleanContent, scoreChange } = stripScoreChange(streamingString);
            
            // Parse the new quiz from the streaming string
            const newQuiz = parseQuizV2(streamingString);
            setActiveQuiz(newQuiz);
            
            // Parse follow-up chips if not in learn mode
            if (!isLearnMode) {
                const parsedChips = parseChips(streamingString);
                setActiveChips(parsedChips || []);
            }
            
            let displayContent = cleanContent;
            if (newQuiz) {
                const quizIdx = displayContent.indexOf('[QUIZ_START]');
                if (quizIdx !== -1) {
                    displayContent = displayContent.substring(0, quizIdx).trim();
                }
            }
            const chipsIdx = displayContent.indexOf('[CHIPS]');
            if (chipsIdx !== -1) {
                displayContent = displayContent.substring(0, chipsIdx).trim();
            }
            const confIdx = displayContent.indexOf('[CONFIDENCE:');
            if (confIdx !== -1) {
                displayContent = displayContent.substring(0, confIdx).trim();
            }
            const score = parseConfidence(streamingString);

            // Update UI message to display sanitized content
            setMessages(prev => prev.map(m => m.id === tempId ? { ...m, content: displayContent, confidence: score } : m));

            // Final sync of progress back to state and database
            if (isLearnMode && activeLP) {
                let nextScore = activeLP.mastery_score || 0;
                let nextPhase = activeLP.phase || 1;
                let feedback = activeLP.last_feedback || '';

                if (scoreChange !== null) {
                    if (scoreChange > 0) {
                        nextScore += scoreChange;
                        feedback = "Correct! Great job.";
                        if (nextScore >= 100) {
                            if (nextPhase < 4) {
                                nextPhase += 1;
                                nextScore = 10;
                                feedback = `Outstanding! You completed Phase ${nextPhase - 1}. Advancing to Phase ${nextPhase}!`;
                            } else {
                                nextScore = 100;
                                feedback = "Congratulations! You have mastered all phases of this topic!";
                            }
                        }
                    } else {
                        nextScore = Math.max(0, nextScore + scoreChange);
                        feedback = "Incorrect. Let's re-review this concept.";
                    }
                }

                // Look back to find the type of the quiz that was answered
                let lastQuizType = 'multiple_choice';
                for (let i = activeMessages.length - 1; i >= 0; i--) {
                    if (activeMessages[i].role === 'assistant') {
                        const parsedPrev = parseQuizV2(activeMessages[i].content);
                        if (parsedPrev) {
                            lastQuizType = parsedPrev.type;
                            break;
                        }
                    }
                }

                const topicIdNorm = normalizeTopicId(activeLP.topic);

                // Run SM-2 spacing if there was a score change
                let sm2Updates = {};
                if (scoreChange !== null) {
                    const isCorrect = scoreChange > 0;
                    
                    // Fetch current SM-2 values
                    let interval = 0;
                    let easeFactor = 2.5;
                    let repetitions = 0;
                    
                    if (allTopicsProgress[topicIdNorm]) {
                        const existing = allTopicsProgress[topicIdNorm];
                        interval = existing.interval || 0;
                        easeFactor = existing.ease_factor || 2.5;
                        repetitions = existing.repetitions || 0;
                    }
                    
                    const q = isCorrect ? 5 : 1;
                    const sm2Result = calculateSM2(q, interval, easeFactor, repetitions);
                    
                    sm2Updates = {
                        interval: sm2Result.interval,
                        ease_factor: sm2Result.easeFactor,
                        repetitions: sm2Result.repetitions,
                        dueDate: sm2Result.dueDate
                    };

                    // Update stats (streak & achievements)
                    await updateStreakAndStats(isCorrect, lastQuizType, activeLP.topic, nextScore);
                }

                const updatedLP = {
                    ...activeLP,
                    phase: nextPhase,
                    mastery_score: nextScore,
                    last_question: newQuiz ? newQuiz.question : (activeLP.last_question || ''),
                    correct_answer_key: newQuiz ? newQuiz.answer : (activeLP.correct_answer_key || ''),
                    last_feedback: feedback,
                    active_quiz_type: newQuiz ? newQuiz.type : null,
                    active_quiz_options: newQuiz ? newQuiz.options : null,
                    active_quiz_expected: newQuiz ? newQuiz.expected : null,
                    ...sm2Updates
                };

                setLearnProgress(updatedLP);

                // Update the local list of all topics progress
                setAllTopicsProgress(prev => ({
                    ...prev,
                    [topicIdNorm]: updatedLP
                }));

                if (currentUser) {
                    const progressRef = doc(db, 'users', currentUser.uid, 'learn_progress', topicIdNorm);
                    await setDoc(progressRef, updatedLP, { merge: true });
                }
            }

        } catch (error) {
            streamingString = `ERROR: CONNECTION FAILED.\n\n[DIAGNOSTIC: ${error.message}]`;
            const errorContent = streamingString;
            setMessages(prev => prev.map(m => m.id === tempId ? { ...m, content: errorContent } : m));
        } finally {
            setIsLoading(false);
            const { text: cleanContent } = stripScoreChange(streamingString);
            let displayContent = cleanContent;
            const quizIdx = displayContent.indexOf('[QUIZ_START]');
            if (quizIdx !== -1) {
                displayContent = displayContent.substring(0, quizIdx).trim();
            }
            const chipsIdx = displayContent.indexOf('[CHIPS]');
            if (chipsIdx !== -1) {
                displayContent = displayContent.substring(0, chipsIdx).trim();
            }
            const confIdx = displayContent.indexOf('[CONFIDENCE:');
            if (confIdx !== -1) {
                displayContent = displayContent.substring(0, confIdx).trim();
            }
            const score = parseConfidence(streamingString);

            // Attach previousContent and parentId to the message if this was a regeneration
            setMessages(prev => prev.map(m => m.id === tempId ? {
                ...m,
                content: displayContent,
                confidence: score,
                previousContent: previousContent || null,
                parentId: m.parentId !== undefined ? m.parentId : null
            } : m));

            if (currentUser) {
                const assistantMsg = { id: tempId, role: 'assistant', content: displayContent, parentId: null };
                if (score !== null) {
                    assistantMsg.confidence = score;
                }
                if (previousContent) {
                    assistantMsg.previousContent = previousContent;
                }
                const updatedMsgs = [...activeMessages, assistantMsg];
                syncToCloud(updatedMsgs, currentChatId);

                if (!streamingString.startsWith('ERROR: CONNECTION FAILED.') && updatedMsgs.length >= 3) {
                    triggerSummarization(updatedMsgs, currentChatId);
                }
            }
        }
    }, [currentUser, currentChatId, isLearnMode, syncToCloud, triggerSummarization, learnProgress, normalizeTopicId, difficulty, allTopicsProgress, updateStreakAndStats]);

    // ── PDF Queue Processor ──
    useEffect(() => {
        if (pdfStatus === 'READY' && queuedMessagesRef.current.length > 0) {
            console.log("📤 PDF READY: Processing queued messages...");
            const queue = [...queuedMessagesRef.current];
            queuedMessagesRef.current = [];
            
            queue.forEach(msgData => {
                fetchAssistantResponse(msgData.activeMessages, msgData.tempId, msgData.model, msgData.userText, msgData.attachment, msgData.customLP);
            });
        }
    }, [pdfStatus, fetchAssistantResponse]);

    // ── PDF: Extract ALL pages + Chunk ──
    const extractAndChunkPdf = async (file) => {
        setPdfStatus('EXTRACTING');
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            
            let fullText = '';
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();
                fullText += content.items.map(item => item.str).join(' ') + '\n\n';
            }

            setPdfStatus('CHUNKING');

            // Chunk into ~800-word segments
            const words = fullText.split(/\s+/);
            const CHUNK_SIZE = 800;
            const chunks = [];
            for (let i = 0; i < words.length; i += CHUNK_SIZE) {
                const chunk = words.slice(i, i + CHUNK_SIZE).join(' ');
                if (chunk.trim()) chunks.push(chunk.trim());
            }

            setPdfStatus('READY');
            pdfChunksRef.current = chunks;
            
            console.log(`📄 PDF VECTORIZED: ${pdf.numPages} pages → ${chunks.length} chunks (${fullText.length} chars)`);
            return chunks;
        } catch (err) {
            console.error("PDF vectorization failed:", err);
            setPdfStatus('IDLE');
            return null;
        }
    };

    const processFile = async (file) => {
        if (!file) return;

        setPdfStatus('IDLE');
        pdfChunksRef.current = null;

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setAttachment({ file, type: 'image', preview: reader.result, base64: reader.result });
            };
        } else if (file.type === 'application/pdf') {
            setAttachment({ file, type: 'pdf', preview: null, base64: null });
            // Vectorize in background — does NOT block sending
            extractAndChunkPdf(file);
        } else {
            alert('UNSUPPORTED FORMAT. PLEASE UPLOAD AN IMAGE OR PDF.');
        }
    };

    const handleFileChange = (e) => {
        processFile(e.target.files[0]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const sendMessage = useCallback(async (customText = null, customLP = null) => {
        setActiveChips([]);
        if (!currentUser && guestQueryCount >= 7) {
            setShowAuthModal(true);
            return;
        }
        const textToUse = (typeof customText === 'string' ? customText : input).trim();
        if (!textToUse && !attachment) return;
        if (isLoading) return;

        const userText = textToUse;
        if (typeof customText !== 'string') {
            setInput('');
        }
        const currentAttachment = attachment;
        
        setAttachment(null);
        setReplyToMsgId(null);
        setIsLoading(true);

        const newUserMsg = {
            id: Date.now(),
            role: 'user',
            content: userText,
            parentId: replyToMsgId,

            attachment: currentAttachment ? { 
                type: currentAttachment.type, 
                name: currentAttachment.file.name
            } : null
        };

        let activeLP = customLP !== null ? customLP : learnProgress;
        if (isLearnMode && !activeLP) {
            let topicText = userText.trim();
            const prefixes = [
                /^(teach me about|learn about|tell me about|how does|what is|tutorial on|guide to)\s+/i,
                /^(teach me|learn|tutorial|guide)\s+/i
            ];
            for (const rx of prefixes) {
                if (rx.test(topicText)) {
                    topicText = topicText.replace(rx, '');
                    break;
                }
            }
            if (topicText.length > 0) {
                topicText = topicText.charAt(0).toUpperCase() + topicText.slice(1);
            } else {
                topicText = "General Topic";
            }
            
            const topicId = normalizeTopicId(topicText);
            
            let loadedProgress = null;
            if (currentUser) {
                try {
                    const progressRef = doc(db, 'users', currentUser.uid, 'learn_progress', topicId);
                    const snap = await getDoc(progressRef);
                    if (snap.exists()) {
                        loadedProgress = snap.data();
                    }
                } catch (e) {
                    console.error("Error checking existing progress:", e);
                }
            }
            
            if (loadedProgress) {
                activeLP = loadedProgress;
            } else {
                activeLP = {
                    topic: topicText,
                    phase: 1,
                    mastery_score: 0,
                    last_question: '',
                    correct_answer_key: '',
                    last_feedback: '',
                    active_quiz_type: null,
                    active_quiz_options: null,
                    active_quiz_expected: null
                };
            }
            setLearnProgress(activeLP);
            if (activeLP.active_quiz_type) {
                setActiveQuiz({
                    type: activeLP.active_quiz_type,
                    question: activeLP.last_question,
                    options: activeLP.active_quiz_options || [],
                    answer: activeLP.correct_answer_key || '',
                    expected: activeLP.active_quiz_expected || ''
                });
            } else {
                setActiveQuiz(null);
            }

            setChatHistory(prev => prev.map(c => {
                if (c.id === currentChatId) {
                    return {
                        ...c,
                        isLearnMode: true,
                        learnTopicId: topicId
                    };
                }
                return c;
            }));
        }

        const activeMessages = [...messages, newUserMsg];
        setMessages(activeMessages);
        syncToCloud(activeMessages, currentChatId, activeLP);

        const tempId = Date.now() + 1;
        
        // Determine model
        let model = 'dolphin-llama3:8b';
        if (currentAttachment?.type === 'image') {
            model = 'llava:7b';
        }

        // Check if we need to queue (PDF is attached but not vectorized yet)
        if (currentAttachment?.type === 'pdf' && pdfStatus !== 'READY') {
            const assistantMsg = { id: tempId, role: 'assistant', content: 'PREPARING CONTEXT...', parentId: null };
            setMessages(prev => [...prev, assistantMsg]);
            queuedMessagesRef.current.push({ activeMessages, tempId, model, userText, attachment: currentAttachment, customLP: activeLP });
            return;
        }

        // Check for split format
        const splitMatch = userText.match(/^split:\s*(.*?)\s*vs\s*(.*?)\s*\|\s*(.*)$/i);
        if (splitMatch) {
            const [, perspA, perspB, query] = splitMatch;
            fetchSplitAssistantResponse(activeMessages, tempId, model, query, perspA, perspB);
        } else {
            // Otherwise, fire normally
            fetchAssistantResponse(activeMessages, tempId, model, userText, currentAttachment, activeLP);
        }
    }, [currentUser, guestQueryCount, input, attachment, replyToMsgId, isLoading, isLearnMode, learnProgress, normalizeTopicId, currentChatId, messages, syncToCloud, pdfStatus, fetchAssistantResponse, fetchSplitAssistantResponse]);

    const startTopic = useCallback(async (node) => {
        if (isLoading) return;
        setIsLearnMode(true);
        
        const topicId = normalizeTopicId(node.id);
        let lp = {
            topic: node.label,
            phase: 1,
            mastery_score: 0,
            last_question: '',
            correct_answer_key: '',
            last_feedback: '',
            active_quiz_type: null,
            active_quiz_options: null,
            active_quiz_expected: null
        };

        if (currentUser) {
            try {
                const progressRef = doc(db, 'users', currentUser.uid, 'learn_progress', topicId);
                const snap = await getDoc(progressRef);
                if (snap.exists()) {
                    lp = snap.data();
                }
            } catch (e) {
                console.error("Failed to load topic progress on node click", e);
            }
        }

        setLearnProgress(lp);
        if (lp.active_quiz_type) {
            setActiveQuiz({
                type: lp.active_quiz_type,
                question: lp.last_question,
                options: lp.active_quiz_options || [],
                answer: lp.correct_answer_key || '',
                expected: lp.active_quiz_expected || ''
            });
        } else {
            setActiveQuiz(null);
        }

        startNewChat(true);

        const promptText = `Teach me about ${node.label}`;
        setInput('');
        await sendMessage(promptText, lp);
    }, [currentUser, isLoading, normalizeTopicId, sendMessage, startNewChat]);

    // Message actions: Copy and Regenerate
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const handleCopyMessage = (msgId, content) => {
        navigator.clipboard.writeText(content);
        setCopiedMessageId(msgId);
        setTimeout(() => setCopiedMessageId(null), 2000);
    };

    const handleRegenerate = async (msgId) => {
        if (isLoading) return;
        const msgIdx = messages.findIndex(m => m.id === msgId);
        if (msgIdx === -1) return;

        const userMsgIdx = msgIdx - 1;
        if (userMsgIdx < 0 || messages[userMsgIdx].role !== 'user') return;

        const userMsg = messages[userMsgIdx];
        // Capture current content before replacing — used to render diff view
        const previousContent = messages[msgIdx].content;
        const activeMessages = messages.slice(0, msgIdx);
        
        setIsLoading(true);
        // Temporarily hold previousContent on the new message stub so fetchAssistantResponse
        // can attach it once the new response is complete
        setMessages([...activeMessages, { id: -1, role: 'assistant', content: '', previousContent, parentId: null }]);

        const tempId = Date.now();
        let model = 'dolphin-llama3:8b';
        if (userMsg.attachment?.type === 'image') {
            model = 'llava:7b';
        }

        fetchAssistantResponse(activeMessages, tempId, model, userMsg.content, userMsg.attachment, previousContent);
    };

    const handleReactToMessage = useCallback(async (msgId, reactionType) => {
        setMessages(prev => {
            const updated = prev.map(m => {
                if (m.id === msgId) {
                    const nextReaction = m.reaction === reactionType ? null : reactionType;
                    return { ...m, reaction: nextReaction };
                }
                return m;
            });
            if (currentUser) {
                syncToCloud(updated, currentChatId);
            }
            return updated;
        });
    }, [currentUser, currentChatId, syncToCloud]);

    // Accept diff: clear previousContent, keep new content
    const handleAcceptDiff = useCallback((msgId) => {
        setMessages(prev => {
            const updated = prev.map(m => m.id === msgId ? { ...m, previousContent: null } : m);
            if (currentUser) syncToCloud(updated, currentChatId);
            return updated;
        });
    }, [currentUser, currentChatId, syncToCloud]);

    // Revert diff: restore previousContent as the new content, clear previousContent
    const handleRevertDiff = useCallback((msgId) => {
        setMessages(prev => {
            const updated = prev.map(m => {
                if (m.id === msgId && m.previousContent) {
                    return { ...m, content: m.previousContent, previousContent: null };
                }
                return m;
            });
            if (currentUser) syncToCloud(updated, currentChatId);
            return updated;
        });
    }, [currentUser, currentChatId, syncToCloud]);

    const processContent = (text) => {
        if (!text) return "";
        let processed = text.replace(/<think>([\s\S]*?)<\/think>/g, (match, p1) => {
            return `\n\n> **[ISHO_THINKING_PROCESS]**\n> ${p1.replace(/\n/g, '\n> ')}\n\n`;
        });
        if (processed.includes('<think>')) {
            processed = processed.replace(/<think>/g, '\n\n> **[ISHO_THINKING_PROCESS]**\n> ');
            processed = processed.replace(/\n/g, '\n> '); 
        }
        return processed;
    };
    const markdownComponents = {
        p: ({node, ...props}) => <p className="font-sans text-[15px] md:text-base leading-relaxed text-white/90 mb-6 whitespace-pre-wrap tracking-wide" {...props} />,
        h1: ({node, children, ...props}) => <h1 className="font-sans font-extrabold text-3xl md:text-4xl text-white mb-6 mt-10 tracking-tight" {...props}>{children}</h1>,
        h2: ({node, children, ...props}) => <h2 className="font-sans font-bold text-2xl md:text-3xl text-white mb-5 mt-8 tracking-tight" {...props}>{children}</h2>,
        h3: ({node, children, ...props}) => <h3 className="font-sans font-bold text-xl md:text-2xl text-pink-50 mb-4 mt-6 tracking-tight" {...props}><span className="inline-block w-2 h-2 rounded-full bg-pink-400 mr-3 shadow-[0_0_8px_rgba(244,114,182,0.5)] relative -top-0.5" />{children}</h3>,
        ul: ({node, ...props}) => <ul className="list-none pl-4 mb-6 text-white/90 space-y-3 font-sans text-[15px] md:text-base" {...props} />,
        ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-6 text-white/90 space-y-3 font-sans text-[15px] md:text-base marker:text-pink-400" {...props} />,
        li: ({node, children, ...props}) => <li className="pl-2 leading-relaxed whitespace-pre-wrap relative" {...props}><span className="absolute left-[-12px] top-[10px] w-[5px] h-[5px] rounded-full bg-pink-400/50" />{children}</li>,
        a: ({node, children, ...props}) => <a className="text-pink-400 hover:text-pink-300 transition-colors underline decoration-pink-400/30 font-semibold" {...props}>{children}</a>,
        strong: ({node, ...props}) => <strong className="font-bold text-white" {...props} />,
        em: ({node, ...props}) => <em className="italic text-pink-300/90" {...props} />,
        blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-pink-400/30 bg-pink-400/[0.03] backdrop-blur-sm py-4 px-6 rounded-r-2xl font-mono text-sm leading-relaxed text-white/70 mb-6" {...props} />,
        table: ({node, ...props}) => (
            <div className="isho-table-wrapper my-6">
                <table className="isho-table-enhanced" {...props} />
            </div>
        ),
        thead: ({node, ...props}) => <thead className="isho-table-head" {...props} />,
        tbody: ({node, ...props}) => <tbody className="isho-table-body" {...props} />,
        tr: ({node, ...props}) => <tr className="isho-table-row" {...props} />,
        th: ({node, ...props}) => <th className="isho-table-th" {...props} />,
        td: ({node, ...props}) => <td className="isho-table-td" {...props} />,
        hr: ({node, ...props}) => <hr className="isho-section-divider" {...props} />,
        code({node, inline, className, children, ...props}) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
                <div className="isho-code-block my-8 shadow-2xl">
                    <div className="isho-code-header">
                        <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-pink-400/50" />
                            {match[1]}
                        </span>
                        <button 
                            onClick={e => {
                                navigator.clipboard.writeText(String(children).replace(/\n$/, ''));
                                e.currentTarget.innerText = 'COPIED!';
                                setTimeout(() => e.currentTarget.innerText = 'COPY', 2000);
                            }}
                            className="isho-btn px-3 py-1 text-[10px] tracking-widest"
                        >
                            COPY
                        </button>
                    </div>
                    <pre className="p-6 overflow-x-auto text-[13px] leading-relaxed font-mono text-white/80 m-0 bg-transparent border-none">
                        <code className={className} {...props}>
                            {children}
                        </code>
                    </pre>
                </div>
            ) : (
                <code className="text-pink-300 bg-pink-400/[0.07] backdrop-blur-sm px-2 py-1 rounded-full font-mono text-[0.9em] border border-pink-400/15" {...props}>
                    {children}
                </code>
            )
        }
    };

    const isGuestLimitReached = !currentUser && guestQueryCount >= 7;

    return (
        <main 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className="isho-page isho-dot-universe w-full h-[100dvh] fixed inset-0 z-40 text-white flex flex-row pt-[80px] md:pt-[100px] overflow-hidden"
        >
            
            {/* Dither Background */}
            <div className="absolute inset-0 z-0 opacity-[0.25] pointer-events-none mix-blend-screen overflow-hidden">
                <Dither waveColor={[244/255, 114/255, 182/255]} colorNum={4} waveAmplitude={0.5} waveFrequency={1.5} waveSpeed={0.02} />
            </div>

            {/* Drag & Drop Overlay */}
            <AnimatePresence>
                {isDragging && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-md flex items-center justify-center pointer-events-none"
                    >
                        <div className="isho-glass-heavy p-12 rounded-[3.5rem] border-2 border-dashed border-pink-400/50 flex flex-col items-center gap-8 shadow-[0_0_100px_rgba(244,114,182,0.15)] scale-110">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full border-2 border-pink-400 flex items-center justify-center animate-pulse">
                                    <svg className="w-12 h-12 text-pink-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                                    </svg>
                                </div>
                                <div className="absolute -top-2 -right-2 w-4 h-4 bg-pink-400 rounded-full animate-ping" />
                            </div>
                            <div className="text-center space-y-2">
                                <p className="font-mono text-pink-400 text-2xl font-bold tracking-[0.4em] uppercase">DROP TO UPLOAD</p>
                                <p className="font-mono text-white/40 text-[10px] tracking-[0.2em] uppercase">IMAGE OR PDF PROFILES</p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mobile Backdrop Overlay */}
            <div 
                className={`fixed inset-0 z-[50] bg-black/60 backdrop-blur-sm md:hidden transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setIsSidebarOpen(false)}
            />

            {/* ═══ Left Session Sidebar ═══ */}
            <div className={`fixed inset-y-0 left-0 pt-[80px] md:pt-0 md:relative z-[60] h-full ${isSidebarOpen ? 'w-[280px] md:w-[320px] translate-x-0' : 'w-[280px] md:w-0 -translate-x-full md:translate-x-0'} isho-sidebar backdrop-blur-2xl flex flex-col transition-all duration-300 overflow-hidden shrink-0`}>
                <div className="w-[280px] md:w-[320px] h-full flex flex-col p-4 md:p-6 relative">
                    
                    {/* Close Button */}
                    <button 
                        onClick={() => setIsSidebarOpen(false)} 
                        className={`absolute top-2 right-2 p-2 text-white/40 hover:text-white isho-glass rounded-full transition-all duration-300 z-50 ${!isSidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>

                    <button 
                        onClick={() => { startNewChat(); setIsSidebarOpen(false); }} 
                        className="isho-btn isho-btn-primary w-full py-4 text-xs tracking-widest flex items-center justify-center gap-2 mb-6 mt-8 md:mt-0 shrink-0"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
                        NEW CHAT
                    </button>
                    
                    {/* Sidebar Tab Switcher */}
                    {currentUser && (
                        <div className="flex gap-2 mb-4 shrink-0 font-mono text-[9px]">
                            <button 
                                onClick={() => setSidebarTab('sessions')}
                                className={`flex-1 py-2 text-center border-b-2 tracking-widest uppercase transition-all ${sidebarTab === 'sessions' ? 'border-pink-400 text-pink-400 font-bold' : 'border-transparent text-white/40 hover:text-white/60'}`}
                            >
                                SESSIONS
                            </button>
                            <button 
                                onClick={() => setSidebarTab('bookmarks')}
                                className={`flex-1 py-2 text-center border-b-2 tracking-widest uppercase transition-all ${sidebarTab === 'bookmarks' ? 'border-pink-400 text-pink-400 font-bold' : 'border-transparent text-white/40 hover:text-white/60'}`}
                            >
                                BOOKMARKS ({activeChatObj?.bookmarks?.length || 0})
                            </button>
                        </div>
                    )}

                    {sidebarTab === 'sessions' ? (
                        <>
                            {/* Active Chat Summary HUD */}
                            {currentUser && activeChatObj && (
                                <div className="isho-summary-card shrink-0">
                                    <div className="isho-summary-card-header font-mono text-[9px] text-pink-400/80 uppercase tracking-widest flex items-center justify-between">
                                        <span>SESSION SUMMARY</span>
                                        {isSummarizing && <span className="isho-summary-pulse-dot" />}
                                    </div>
                                    <div className="isho-summary-card-body mt-2">
                                        {activeChatObj.summary ? (
                                            <ul>
                                                {parseSummary(activeChatObj.summary).map((bullet, idx) => (
                                                    <li key={idx}>{bullet}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-white/30 italic text-[10px] leading-relaxed">
                                                {isSummarizing ? 'Generating summary...' : 'No summary generated yet. Send messages to analyze.'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex-1 overflow-y-auto pr-2 isho-scrollbar flex flex-col gap-2" data-lenis-prevent>
                                {!currentUser ? (
                                    <div className="text-center mt-10">
                                        <p className="font-mono text-[10px] text-white/40 mb-4 tracking-widest leading-loose">LOGIN IS CURRENTLY DISABLED</p>
                                    </div>
                                ) : chatHistory.length === 0 ? (
                                    <p className="font-mono text-[10px] text-white/30 text-center mt-10 tracking-widest uppercase">NO RECORDED SESSIONS</p>
                                ) : (
                                    chatHistory.map(chat => (
                                        <button 
                                            key={chat.id} 
                                            onClick={() => { loadChat(chat); setIsSidebarOpen(false); }}
                                            className={`isho-session-item ${currentChatId === chat.id ? 'active' : ''} w-full text-left p-3 pl-5 font-mono text-xs flex items-center gap-3 group transition-all`}
                                        >
                                            <svg className={`w-4 h-4 shrink-0 transition-colors ${currentChatId === chat.id ? 'text-pink-400' : 'text-white/20 group-hover:text-white/40'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                            <div className={`flex-1 truncate ${currentChatId === chat.id ? 'text-white' : 'text-white/50'}`}>{chat.title || 'ARCHIVED SESSION'}</div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 overflow-y-auto pr-2 isho-scrollbar flex flex-col gap-3" data-lenis-prevent>
                            {!activeChatObj?.bookmarks || activeChatObj.bookmarks.length === 0 ? (
                                <div className="text-center mt-10 px-4">
                                    <p className="font-mono text-[10px] text-white/30 uppercase tracking-widest">NO BOOKMARKS</p>
                                    <p className="font-mono text-[8px] text-white/20 mt-2 leading-loose uppercase">HOVER A RESPONSE AND CLICK THE BOOKMARK ICON TO PERSIST IMPORTANT SEGMENTS</p>
                                </div>
                            ) : (
                                activeChatObj.bookmarks.map(b => (
                                    <div 
                                        key={b.id}
                                        onClick={() => { scrollToMessage(b.messageId); if (window.innerWidth < 768) setIsSidebarOpen(false); }}
                                        className="isho-bookmark-item w-full text-left p-3 font-mono border border-white/5 hover:border-pink-400/30 rounded-lg bg-white/[0.01] hover:bg-pink-400/[0.02] cursor-pointer transition-all flex flex-col gap-1 group animate-fadeIn"
                                    >
                                        <div className="flex items-center justify-between w-full gap-2">
                                            <span className="text-pink-400 font-bold text-[10px] tracking-wider truncate flex-1">{b.label}</span>
                                            <span className="text-[8px] text-white/20 shrink-0">{new Date(b.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </div>
                                        <p className="text-[10px] text-white/50 line-clamp-2 mt-1 leading-relaxed">{b.messagePreview}</p>
                                        <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-white/5 text-[8px] text-white/30 group-hover:text-pink-400/60 transition-colors">
                                            <span>JUMP TO MESSAGE ➔</span>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); deleteBookmark(b.id); }} 
                                                className="hover:text-red-400 text-white/30 transition-colors uppercase font-mono tracking-widest bg-transparent border-0 p-0 cursor-pointer text-[8px]"
                                            >
                                                DELETE
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ═══ Main Chat Area ═══ */}
            <div className="flex-1 min-w-0 h-full flex flex-col relative w-full overflow-hidden">
                


                {/* Sidebar Toggle Button */}
                {!isFocusMode && (
                    <button 
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                        className={`absolute top-6 max-md:left-1/2 max-md:-translate-x-1/2 md:left-6 z-50 isho-btn px-5 py-2.5 text-[9px] font-bold tracking-[0.2em] flex items-center justify-center gap-2 ${isSidebarOpen ? 'md:opacity-0 md:pointer-events-none' : 'md:opacity-100 md:pointer-events-auto'} max-md:opacity-100 transition-all duration-300`}
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        SESSIONS
                    </button>
                )}

                {/* ── Scrollable Chat Area ── */}
                <div ref={chatContainerRef} onScroll={handleScroll} className="flex-1 w-full max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto overflow-y-auto px-3 sm:px-4 md:px-8 py-6 flex flex-col gap-6 relative z-10 isho-scrollbar isho-message-column" data-lenis-prevent>
                    
                    {/* Hero Header */}
                    {!isFocusMode && (
                        <div className="isho-hero-dots w-full mb-8 pt-[60px] md:pt-4 flex flex-col items-start gap-3">
                            <img src="/images/isho-logo.png" alt="ISHO Logo" className="h-16 md:h-24 w-auto object-contain filter drop-shadow-[0_0_20px_rgba(244,114,182,0.3)] opacity-90" />
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 font-mono text-[9px] md:text-[10px] text-white/30 uppercase tracking-widest ml-2">
                                <span>● EXPERIMENTAL INTELLIGENCE UPLINK ●</span>
                                <span className="text-pink-400/50 font-bold sm:ml-4">CEREBRAS FREE TIER: {usageCount}/20 CYCLES [{"█".repeat(Math.min(10, Math.round(usageCount / 2)))}${"░".repeat(Math.max(0, 10 - Math.round(usageCount / 2)))}]</span>
                            </div>
                        </div>
                    )}

                    {(isFocusMode && messages.length > 0 ? [messages[messages.length - 1]] : messages).map((msg, idx) => {
                        const originalIdx = isFocusMode && messages.length > 0 ? messages.length - 1 : idx;
                        const isStreaming = isLoading && originalIdx === messages.length - 1 && msg.role === 'assistant';
                        const isBookmarked = activeChatObj?.bookmarks?.some(b => b.messageId === msg.id);
                        let tldrText = null;
                        let partitioned = { intro: msg.content, sections: [] };
                        let bodyText = msg.content;
                        
                        if (msg.role === 'assistant' && !isStreaming && msg.content && msg.id !== 0) {
                            tldrText = extractTldr(msg.content);
                            if (tldrText) {
                                const explicitTldrRegex = /^>\s*\*\*\[?TL;?DR\]?\*\*[\s\S]*?(?=\n\n|\n[^\s>])/mi;
                                const explicitMatch = msg.content.match(explicitTldrRegex);
                                if (explicitMatch) {
                                    bodyText = msg.content.replace(explicitMatch[0], '').trim();
                                }
                            }
                            partitioned = partitionResponseSections(bodyText);
                        }

                        return (
                            <motion.div 
                                ref={el => messageRefs.current[msg.id] = el}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                                key={msg.id} 
                                className={`flex gap-2 sm:gap-3 md:gap-5 w-full group ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'} ${msg.parentId !== null && msg.parentId !== undefined ? 'ml-8 pl-4 border-l-2 border-pink-500/20' : ''}`}
                            >
                                {/* Avatar */}
                                <div className={`isho-avatar w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex-shrink-0 ${
                                    msg.role === 'user' 
                                        ? 'isho-avatar-user' 
                                        : 'isho-avatar-ai'
                                }`}>
                                    {msg.role === 'user' ? (
                                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white/70" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    ) : (
                                        <div className="relative flex items-center justify-center">
                                            <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-pink-400 shadow-[0_0_12px_rgba(244,114,182,0.6)]" />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className={`max-w-[85%] ${
                                    msg.role === 'user' 
                                        ? 'isho-msg-user font-mono text-xs md:text-sm px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5 text-white' 
                                        : 'isho-msg-assistant pl-2 sm:pl-4 pt-1 md:pt-2 w-full text-white'
                                }`}>
                                    {/* ═══ ADAPTIVE LAYOUT BADGE ═══ */}
                                    {msg.role === 'assistant' && msg.meta && msg.meta.label && msg.meta.label !== 'RESPONSE' && msg.content && msg.content.length > 0 && (
                                        <div className="isho-layout-badge">
                                            <span className="isho-layout-badge-dot" />
                                            <span className="isho-layout-badge-text">◆ {msg.meta.label}</span>
                                        </div>
                                    )}

                                    {/* ── Split View Render ── */}
                                    {msg.isSplit && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mt-2">
                                            <div className="isho-split-col">
                                                <div className="isho-split-header">{msg.perspectiveA}</div>
                                                <div className="isho-split-body">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={markdownComponents}
                                                    >
                                                        {msg.contentA}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                            <div className="isho-split-col">
                                                <div className="isho-split-header">{msg.perspectiveB}</div>
                                                <div className="isho-split-body">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={markdownComponents}
                                                    >
                                                        {msg.contentB}
                                                    </ReactMarkdown>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {msg.attachment && (
                                        <div className="isho-attachment-badge inline-flex items-center gap-2 px-3 py-1.5 font-mono text-[10px] text-pink-400 mb-3">
                                            {msg.attachment.type === 'image' ? (
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                            ) : (
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                            )}
                                            <span className="truncate max-w-[180px]">{msg.attachment.name}</span>
                                        </div>
                                    )}
                                    
                                    {msg.role === 'assistant' ? (
                                        msg.content === '' && isLoading ? (
                                            <div className="isho-dot-loader">
                                                <div className="isho-dot-loader-dot" />
                                                <div className="isho-dot-loader-dot" />
                                                <div className="isho-dot-loader-dot" />
                                                <div className="isho-dot-loader-dot" />
                                                <div className="isho-dot-loader-dot" />
                                            </div>
                                        ) : (
                                            <div className={`isho-layout-wrapper ${msg.meta ? `isho-layout-${msg.meta.layout || 'conversation'}` : ''} ${msg.isSplit ? 'hidden' : ''}`}>
                                                {/* Render TL;DR Sticky Banner */}
                                                {tldrText && (
                                                    <div className="isho-tldr-banner my-4">
                                                        <div className="isho-tldr-header">
                                                            <span className="isho-tldr-dot" />
                                                            <span className="isho-tldr-title">SYSTEM TL;DR SUMMARY</span>
                                                        </div>
                                                        <p className="isho-tldr-content">{tldrText}</p>
                                                    </div>
                                                )}

                                                {/* Render Plain Markdown for intro / streaming / single section responses */}
                                                {(isStreaming || partitioned.sections.length <= 1) ? (
                                                    (() => {
                                                        const quiz = !isStreaming ? parseQuiz(bodyText) : null;
                                                        if (quiz) {
                                                            return (
                                                                <>
                                                                    <ReactMarkdown 
                                                                        remarkPlugins={[remarkGfm]}
                                                                        components={markdownComponents}
                                                                    >
                                                                        {processContent(quiz.lessonText)}
                                                                    </ReactMarkdown>
                                                                    <div className="isho-quiz-panel mt-6 p-6 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex flex-col gap-4">
                                                                        <div className="flex items-center gap-2 text-[10px] font-mono text-pink-400 font-bold tracking-widest uppercase">
                                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                                                                            </svg>
                                                                            KNOWLEDGE CHECK
                                                                        </div>
                                                                        <p className="font-sans text-sm md:text-base font-medium text-white/90 leading-relaxed">
                                                                            {quiz.question}
                                                                        </p>
                                                                        <div className="flex flex-col gap-3 mt-2">
                                                                            {quiz.options.map(opt => {
                                                                                const nextMsg = messages[idx + 1];
                                                                                const chosenKey = (nextMsg && nextMsg.role === 'user') ? nextMsg.content.trim().toUpperCase() : null;
                                                                                const isSelected = chosenKey === opt.key;
                                                                                const isAnySelected = chosenKey !== null;
                                                                                const isInteractable = idx === messages.length - 1 && !isLoading;

                                                                                return (
                                                                                    <button
                                                                                        key={opt.key}
                                                                                        disabled={!isInteractable}
                                                                                        onClick={() => sendMessage(opt.key)}
                                                                                        className={`isho-quiz-btn flex items-center gap-4 text-left p-4 rounded-xl border transition-all duration-300 font-sans text-xs md:text-sm group ${
                                                                                            isSelected 
                                                                                                ? 'border-pink-500 bg-pink-500/10 text-white shadow-[0_0_15px_rgba(244,114,182,0.15)] font-bold' 
                                                                                                : isAnySelected 
                                                                                                    ? 'border-white/5 bg-white/[0.01] text-white/40 opacity-60' 
                                                                                                    : isInteractable
                                                                                                        ? 'border-white/10 bg-white/[0.02] text-white hover:border-pink-400/40 hover:bg-white/[0.04] hover:shadow-[0_4px_12px_rgba(244,114,182,0.02)] cursor-pointer'
                                                                                                        : 'border-white/5 bg-white/[0.01] text-white/60'
                                                                                        }`}
                                                                                    >
                                                                                        <span className={`isho-quiz-key w-6 h-6 rounded-md flex items-center justify-center font-mono font-bold text-xs shrink-0 transition-colors ${
                                                                                            isSelected 
                                                                                                ? 'bg-pink-500 text-black font-extrabold' 
                                                                                                : isAnySelected
                                                                                                    ? 'bg-white/5 text-white/20'
                                                                                                    : isInteractable
                                                                                                        ? 'bg-white/10 text-white/70 group-hover:bg-pink-400/20 group-hover:text-pink-400'
                                                                                                        : 'bg-white/5 text-white/40'
                                                                                        }`}>
                                                                                            {opt.key}
                                                                                        </span>
                                                                                        <span className="flex-1 font-medium">{opt.text}</span>
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            );
                                                        }
                                                        return (
                                                            <ReactMarkdown 
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {processContent(isStreaming && showCursor ? bodyText + '▊' : bodyText)}
                                                            </ReactMarkdown>
                                                        );
                                                    })()
                                                ) : (
                                                    <>
                                                        {partitioned.intro && (
                                                            <ReactMarkdown 
                                                                remarkPlugins={[remarkGfm]}
                                                                components={markdownComponents}
                                                            >
                                                                {processContent(partitioned.intro)}
                                                            </ReactMarkdown>
                                                        )}

                                                        <div className="isho-accordion-container space-y-3 mt-4">
                                                            {partitioned.sections.map((sec, secIdx) => {
                                                                const isExpanded = !!expandedSections[msg.id]?.[secIdx];
                                                                return (
                                                                    <div key={secIdx} className={`isho-accordion-item ${isExpanded ? 'expanded' : ''}`}>
                                                                        <button 
                                                                            onClick={() => {
                                                                                setExpandedSections(prev => {
                                                                                    const msgMap = prev[msg.id] || {};
                                                                                    return {
                                                                                        ...prev,
                                                                                        [msg.id]: {
                                                                                            ...msgMap,
                                                                                            [secIdx]: !msgMap[secIdx]
                                                                                        }
                                                                                    };
                                                                                });
                                                                            }}
                                                                            className="isho-accordion-trigger"
                                                                        >
                                                                            <span className="isho-accordion-title">◆ {sec.title}</span>
                                                                            <svg className={`w-4 h-4 isho-accordion-icon ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                                            </svg>
                                                                        </button>
                                                                        <AnimatePresence initial={false}>
                                                                            {isExpanded && (
                                                                                <motion.div
                                                                                    initial={{ height: 0, opacity: 0 }}
                                                                                    animate={{ height: 'auto', opacity: 1 }}
                                                                                    exit={{ height: 0, opacity: 0 }}
                                                                                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                                                                                    className="overflow-hidden"
                                                                                >
                                                                                    <div className="isho-accordion-content pt-4 pb-2 px-1">
                                                                                        <ReactMarkdown 
                                                                                            remarkPlugins={[remarkGfm]}
                                                                                            components={markdownComponents}
                                                                                        >
                                                                                            {processContent(sec.content)}
                                                                                        </ReactMarkdown>
                                                                                    </div>
                                                                                </motion.div>
                                                                            )}
                                                                        </AnimatePresence>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                )}

                                                {/* Bookmark Input Form or Action Toolbar */}
                                                {msg.id !== 0 && !isStreaming && (
                                                    activeBookmarkInputMsgId === msg.id ? (
                                                        <div className="isho-bookmark-input-wrap flex items-center gap-2 mt-4 w-full max-w-sm">
                                                            <input 
                                                                type="text" 
                                                                placeholder="Name this bookmark..." 
                                                                value={bookmarkLabel} 
                                                                onChange={(e) => setBookmarkLabel(e.target.value)} 
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        saveBookmark(msg.id, msg.content);
                                                                    } else if (e.key === 'Escape') {
                                                                        setActiveBookmarkInputMsgId(null);
                                                                        setBookmarkLabel('');
                                                                    }
                                                                }}
                                                                className="isho-input flex-1 px-3 py-1.5 text-xs font-mono bg-black/40 border border-white/10 rounded focus:border-pink-400/60 focus:outline-none text-white placeholder-white/20"
                                                                autoFocus
                                                            />
                                                            <button 
                                                                onClick={() => saveBookmark(msg.id, msg.content)}
                                                                className="isho-btn px-3 py-1.5 text-[9px] font-mono tracking-wider bg-pink-500/20 border border-pink-500/40 text-pink-400 hover:bg-pink-500/30 transition-all"
                                                            >
                                                                SAVE
                                                            </button>
                                                            <button 
                                                                onClick={() => { setActiveBookmarkInputMsgId(null); setBookmarkLabel(''); }}
                                                                className="isho-btn px-2 py-1.5 text-[9px] font-mono tracking-wider bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 transition-all"
                                                            >
                                                                CANCEL
                                                            </button>
                                                        </div>
                                                    ) : (
                                                         <div className={`isho-msg-actions flex items-center gap-3 mt-4 transition-opacity duration-200 ${msg.reaction || isBookmarked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                             {/* Thumbs Up (Like) */}
                                                             <button 
                                                                 onClick={() => handleReactToMessage(msg.id, 'like')} 
                                                                 className={`isho-action-btn flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider ${
                                                                     msg.reaction === 'like' ? 'isho-reaction-active' : ''
                                                                 }`}
                                                                 title="Thumbs Up"
                                                             >
                                                                 <svg className="w-3.5 h-3.5" fill={msg.reaction === 'like' ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                     <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.725-.359 1.561-.5 2.338-.326a1.5 1.5 0 011.08 1.964L13.75 10.5h6.116a1.5 1.5 0 011.498 1.515 4.697 4.697 0 01-1.854 3.738C18.9 16.208 18 17.5 18 19.5a1.5 1.5 0 01-1.5 1.5h-5c-.772 0-1.52-.308-2.072-.856l-3.32-3.32a.75.75 0 00-1.06 0l-.354.354a.75.75 0 01-1.06 0l-1.875-1.875a.75.75 0 010-1.06l1.875-1.875a.75.75 0 011.06 0l.354.354a.75.75 0 001.06 0l2.7-2.7z" />
                                                                 </svg>
                                                             </button>
                                                             
                                                             {/* Thumbs Down (Dislike) */}
                                                             <button 
                                                                 onClick={() => handleReactToMessage(msg.id, 'dislike')} 
                                                                 className={`isho-action-btn flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider ${
                                                                     msg.reaction === 'dislike' ? 'isho-reaction-active' : ''
                                                                 }`}
                                                                 title="Thumbs Down"
                                                             >
                                                                 <svg className="w-3.5 h-3.5" fill={msg.reaction === 'dislike' ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                     <path strokeLinecap="round" strokeLinejoin="round" d="M17.367 13.5c-.806 0-1.533.446-2.031 1.08a9.041 9.041 0 01-2.861 2.4c-.725.359-1.561.5-2.338.326a1.5 1.5 0 01-1.08-1.964l1.202-3.842H4.143a1.5 1.5 0 01-1.498-1.515 4.697 4.697 0 011.854-3.738C5.1 7.792 6 6.5 6 4.5A1.5 1.5 0 017.5 3h5c.772 0 1.52.308 2.072.856l3.32 3.32a.75.75 0 001.06 0l.354-.354a.75.75 0 011.06 0l1.875 1.875a.75.75 0 010 1.06l-1.875 1.875a.75.75 0 01-1.06 0l-.354-.354a.75.75 0 00-1.06 0l-2.7 2.7z" />
                                                                 </svg>
                                                             </button>
 
                                                             <button 
                                                                 onClick={() => handleCopyMessage(msg.id, msg.content)} 
                                                                 className="isho-action-btn flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider"
                                                                 title="Copy response to clipboard"
                                                             >
                                                                 {copiedMessageId === msg.id ? (
                                                                     <>
                                                                         <svg className="w-3.5 h-3.5 text-pink-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                             <polyline points="20 6 9 17 4 12"/>
                                                                         </svg>
                                                                         <span className="text-pink-400">COPIED</span>
                                                                     </>
                                                                 ) : (
                                                                     <>
                                                                         <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                             <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                                                             <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                                                         </svg>
                                                                         <span>COPY</span>
                                                                     </>
                                                                 )}
                                                             </button>
                                                             
                                                             {msg.role === 'assistant' && (
                                                                 <>
                                                                     <button 
                                                                         onClick={() => handleRegenerate(msg.id)} 
                                                                         className="isho-action-btn flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider"
                                                                         title="Regenerate response"
                                                                     >
                                                                         <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                             <path d="M23 4v6h-6M1 20v-6h6"/>
                                                                             <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                                                         </svg>
                                                                         <span>REGENERATE</span>
                                                                     </button>
                                                                     <button 
                                                                         onClick={() => setReplyToMsgId(msg.id)} 
                                                                         className="isho-action-btn flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider"
                                                                         title="Reply to this message"
                                                                     >
                                                                         <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                                                                         <span>REPLY</span>
                                                                     </button>
                                                                 </>
                                                             )}

                                                             <button 
                                                                 onClick={() => {
                                                                     setActiveBookmarkInputMsgId(msg.id);
                                                                     const existing = activeChatObj?.bookmarks?.find(b => b.messageId === msg.id);
                                                                     setBookmarkLabel(existing ? existing.label : '');
                                                                 }}
                                                                 className={`isho-action-btn flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider ${
                                                                     isBookmarked ? 'isho-reaction-active' : ''
                                                                 }`}
                                                                 title={isBookmarked ? "Rename or update bookmark" : "Bookmark this message"}
                                                             >
                                                                 <svg className="w-3.5 h-3.5" fill={isBookmarked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                     <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                                                                 </svg>
                                                                 <span>{isBookmarked ? 'BOOKMARKED' : 'BOOKMARK'}</span>
                                                             </button>
                                                         </div>
                                                     )
                                                 )}

                                                 {/* Confidence Indicator Bar */}
                                                 {msg.confidence !== undefined && msg.confidence !== null && (
                                                     <div className="isho-confidence-container mt-3 px-1">
                                                         <div className="flex items-center justify-between text-[9px] font-mono text-white/30 mb-1">
                                                             <span>RESPONSE CONFIDENCE</span>
                                                             <span className={
                                                                 msg.confidence >= 70 ? 'text-green-400' :
                                                                 msg.confidence >= 40 ? 'text-amber-400' : 'text-red-400'
                                                             }>{msg.confidence}%</span>
                                                         </div>
                                                         <div className="isho-confidence-bar-bg w-full h-[3px] bg-white/5 rounded-full overflow-hidden">
                                                             <div 
                                                                 className={`isho-confidence-bar-fill h-full rounded-full transition-all duration-500 ${
                                                                     msg.confidence >= 70 ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' :
                                                                     msg.confidence >= 40 ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]' : 
                                                                     'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]'
                                                                 }`}
                                                                 style={{ width: `${msg.confidence}%` }}
                                                             />
                                                         </div>
                                                     </div>
                                                 )}

                                                 {/* ── Diff View (shown after Regenerate) ── */}
                                                 {msg.previousContent && !isStreaming && (
                                                     <div className="isho-diff-container mt-4">
                                                         <div className="flex items-center justify-between mb-2">
                                                             <span className="text-[9px] font-mono tracking-[0.2em] text-white/30 uppercase">Response Diff</span>
                                                             <div className="flex items-center gap-2">
                                                                 <button
                                                                     onClick={() => handleRevertDiff(msg.id)}
                                                                     className="isho-diff-btn isho-diff-btn--revert text-[9px] font-mono tracking-wider px-2.5 py-1"
                                                                 >
                                                                     ↩ REVERT
                                                                 </button>
                                                                 <button
                                                                     onClick={() => handleAcceptDiff(msg.id)}
                                                                     className="isho-diff-btn isho-diff-btn--accept text-[9px] font-mono tracking-wider px-2.5 py-1"
                                                                 >
                                                                     ✓ ACCEPT
                                                                 </button>
                                                             </div>
                                                         </div>
                                                         <div className="isho-diff-body font-mono text-xs leading-relaxed">
                                                             {diffWords(msg.previousContent, msg.content).map((part, i) => (
                                                                 <span
                                                                     key={i}
                                                                     className={
                                                                         part.added ? 'isho-diff-added' :
                                                                         part.removed ? 'isho-diff-removed' :
                                                                         'isho-diff-unchanged'
                                                                     }
                                                                 >{part.value}</span>
                                                             ))}
                                                         </div>
                                                     </div>
                                                 )}
                                            </div>
                                        )
                                    ) : (
                                        <p className="whitespace-pre-wrap m-0 font-mono text-xs md:text-sm">{msg.content}</p>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                    <div ref={scrollAnchorRef} className="h-2 w-full clear-both" />
                </div>

                {/* Floating Jump to Bottom Button */}
                <AnimatePresence>
                    {showScrollButton && (
                        <motion.button
                            initial={{ opacity: 0, y: 10, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.9 }}
                            onClick={() => {
                                isAutoScrollRef.current = true;
                                scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
                                setShowScrollButton(false);
                            }}
                            className="isho-jump-bottom-btn"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                            <span>Jump to Bottom</span>
                        </motion.button>
                    )}
                </AnimatePresence>

                {/* ═══ Input Container ═══ */}
                <div className="w-full shrink-0 px-3 sm:px-4 md:px-8 pb-6 md:pb-8 pt-2 relative z-20 flex justify-center isho-bottom-fade">
                    <div className="w-full max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto relative isho-message-column">
                        
                        {/* Attachment Preview */}
                        <AnimatePresence>
                            {attachment && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="isho-attachment-badge absolute bottom-[calc(100%+0.5rem)] left-0 flex items-center gap-3 text-pink-300 px-3 py-1.5 font-mono text-[10px] md:text-xs overflow-hidden"
                                >
                                    {attachment.type === 'image' && attachment.preview ? (
                                        <img src={attachment.preview} alt="attached img" className="w-6 h-6 md:w-8 md:h-8 object-cover rounded shadow" />
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                    )}
                                    <span className="max-w-[150px] md:max-w-[200px] truncate">{attachment.file.name}</span>
                                    
                                    {/* PDF Vectorization Status */}
                                    {attachment.type === 'pdf' && pdfStatus !== 'IDLE' && pdfStatus !== 'READY' && (
                                        <span className="isho-vectorize-status">
                                            <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
                                            {pdfStatus === 'EXTRACTING' ? 'EXTRACTING...' : 'VECTORIZING...'}
                                        </span>
                                    )}

                                    {pdfStatus === 'READY' && (
                                        <span className="text-[9px] text-pink-400 font-mono tracking-widest flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.5)]" />
                                            VECTORIZED
                                        </span>
                                    )}

                                    <button onClick={() => { setAttachment(null); setPdfStatus('IDLE'); pdfChunksRef.current = null; }} className="ml-1 hover:text-white p-1 bg-black/30 rounded-full transition-colors">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Guest query counter / warning banner */}
                        {!currentUser && (
                            <div className="mb-3 w-full flex justify-center">
                                {isGuestLimitReached ? (
                                    <div className="w-full p-4 rounded-xl border border-pink-500/40 bg-pink-950/20 backdrop-blur-md flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left shadow-[0_0_20px_rgba(244,114,182,0.1)]">
                                        <div className="flex flex-col gap-1">
                                            <span className="font-mono text-xs font-bold text-pink-400 tracking-wider">LIMIT REACHED: 7 OF 7 GUEST QUERIES UTILIZED</span>
                                            <span className="font-sans text-xs text-white/60">Sign in to unlock unlimited high-performance queries and preserve your chat sessions.</span>
                                        </div>
                                        <button className="isho-btn isho-btn-primary px-6 py-2 text-xs font-mono tracking-widest shrink-0 cursor-not-allowed opacity-50">
                                            SIGN IN TO ISHO
                                        </button>
                                    </div>
                                ) : guestQueryCount === 6 ? (
                                    <div className="w-full px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-950/10 backdrop-blur-sm flex items-center justify-between gap-3 text-left shadow-[0_0_15px_rgba(239,68,68,0.05)]">
                                        <span className="font-mono text-[10px] text-red-400 font-bold tracking-widest uppercase flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                            CRITICAL: 1 GUEST QUERY REMAINING! SIGN IN TO PRESERVE HISTORY.
                                        </span>

                                    </div>
                                ) : guestQueryCount >= 4 ? (
                                    <div className="w-full px-4 py-2.5 rounded-lg border border-amber-500/30 bg-amber-950/10 backdrop-blur-sm flex items-center justify-between gap-3 text-left shadow-[0_0_15px_rgba(245,158,11,0.05)]">
                                        <span className="font-mono text-[10px] text-amber-400 font-bold tracking-widest uppercase">
                                            {7 - guestQueryCount} GUEST QUERIES REMAINING. SIGN IN TO UNLOCK UNLIMITED QUERIES.
                                        </span>

                                    </div>
                                ) : (
                                    <div className="px-3 py-1.5 rounded-md border border-white/5 bg-white/[0.01] backdrop-blur-sm">
                                        <span className="font-mono text-[9px] text-white/30 tracking-wider">
                                            GUEST UPLINK: {7 - guestQueryCount} OF 7 QUERIES REMAINING
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Follow-up suggested chips */}
                        {activeChips.length > 0 && !isLoading && (
                            <div className="flex flex-wrap gap-2 justify-center mb-4 relative z-10 w-full">
                                {activeChips.map((chip, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => sendMessage(chip)}
                                        className="isho-followup-chip"
                                    >
                                        {chip}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Input Box */}
                        <div className={`isho-input-container w-full flex flex-col group ${isGuestLimitReached ? 'opacity-50 pointer-events-none' : ''}`}>
                            {showSplitModal && (
                                <div className="px-4 py-3 border-b border-white/[0.04] bg-white/[0.02] flex flex-col gap-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-[10px] font-mono text-white/50 tracking-widest">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                                            <span>SPLIT PERSPECTIVE VIEW</span>
                                        </div>
                                        <button onClick={() => setShowSplitModal(false)} className="text-white/30 hover:text-white/80 transition-colors">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    </div>
                                    <div className="flex gap-4 items-center">
                                        <div className="flex-1 flex flex-col gap-1">
                                            <label className="text-[9px] font-mono text-pink-400/80 uppercase">Perspective A</label>
                                            <input 
                                                value={perspectiveA} onChange={e => setPerspectiveA(e.target.value)} 
                                                className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-pink-500/50" 
                                                placeholder="e.g. Senior Engineer" 
                                            />
                                        </div>
                                        <div className="text-white/20 font-mono text-xs font-bold pt-4">VS</div>
                                        <div className="flex-1 flex flex-col gap-1">
                                            <label className="text-[9px] font-mono text-cyan-400/80 uppercase">Perspective B</label>
                                            <input 
                                                value={perspectiveB} onChange={e => setPerspectiveB(e.target.value)} 
                                                className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-cyan-500/50" 
                                                placeholder="e.g. Product Manager" 
                                            />
                                        </div>
                                        <div className="pt-4">
                                            <button 
                                                onClick={() => {
                                                    const text = input.trim() || 'Please explain this concept.';
                                                    setInput(`split: ${perspectiveA} vs ${perspectiveB} | ${text}`);
                                                    setShowSplitModal(false);
                                                }}
                                                className="isho-btn px-4 py-1.5 text-[10px] whitespace-nowrap bg-white/5 border border-white/10 hover:bg-white/10"
                                            >
                                                SPLIT ANSWER
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {replyToMsgId && (
                                <div className="px-4 py-2 border-b border-white/[0.04] bg-white/[0.02] flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-[10px] font-mono text-white/50">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                                        <span>REPLYING TO:</span>
                                        <span className="text-white/80 truncate max-w-[200px] sm:max-w-[400px]">
                                            {messages.find(m => m.id === replyToMsgId)?.content?.substring(0, 40) || ''}...
                                        </span>
                                    </div>
                                    <button 
                                        onClick={() => setReplyToMsgId(null)}
                                        className="text-white/30 hover:text-white/80 transition-colors"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            )}
                            <textarea
                                ref={textareaRef}
                                disabled={isGuestLimitReached}
                                className="w-full bg-transparent border-none text-white font-mono text-xs md:text-sm px-4 sm:px-6 py-4 sm:py-5 md:py-6 overflow-y-auto focus:outline-none resize-none placeholder:text-white/20 placeholder:uppercase disabled:cursor-not-allowed"
                                placeholder={isGuestLimitReached ? "● GUEST LIMIT EXHAUSTED. SIGN IN TO CONTINUE." : "● TRANSMIT YOUR QUERY TO ISHO..."}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={1}
                                style={{ minHeight: '60px', maxHeight: '200px' }}
                            />
                            
                            <div className="flex justify-between items-center px-3 sm:px-4 py-2 sm:py-3 border-t border-white/[0.04]">
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => fileInputRef.current?.click()} 
                                        disabled={isGuestLimitReached}
                                        className="isho-btn px-3 py-1.5 md:py-2 text-[9px] flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                        <span className="hidden sm:inline">ATTACH</span>
                                    </button>
                                    <button 
                                        onClick={() => setShowSplitModal(true)} 
                                        disabled={isGuestLimitReached}
                                        className="isho-btn px-3 py-1.5 md:py-2 text-[9px] flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                                        <span className="hidden sm:inline">SPLIT</span>
                                    </button>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        className="hidden" 
                                        accept="image/*,application/pdf"
                                        onChange={handleFileChange}
                                        disabled={isGuestLimitReached}
                                    />
                                    
                                    <div className="isho-glass-divider w-[1px] h-4 mx-1" />
 
                                    <button 
                                        onClick={() => setIsLearnMode(!isLearnMode)}
                                        disabled={isGuestLimitReached}
                                        className={`isho-btn px-3 py-1.5 md:py-2 text-[9px] flex items-center gap-2 ${isLearnMode ? 'isho-learn-active' : ''} disabled:opacity-30 disabled:cursor-not-allowed`}
                                    >
                                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>
                                        <span className="hidden sm:inline">
                                            {isLearnMode ? 'LEARN ●' : 'LEARN ○'}
                                        </span>
                                    </button>

                                    <button 
                                        onClick={() => setIsFocusMode(!isFocusMode)}
                                        disabled={isGuestLimitReached}
                                        className={`isho-btn px-3 py-1.5 md:py-2 text-[9px] flex items-center gap-2 ${isFocusMode ? 'isho-focus-active' : ''} disabled:opacity-30 disabled:cursor-not-allowed`}
                                        title="Toggle Focus Mode (Alt + F)"
                                    >
                                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="hidden sm:inline">
                                            {isFocusMode ? 'FOCUS ●' : 'FOCUS ○'}
                                        </span>
                                    </button>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-[8px] md:text-[9px] uppercase tracking-widest text-white/20 hidden md:inline">
                                        SHIFT + ENTER FOR NEWLINE
                                    </span>
                                    <button 
                                        className="isho-btn isho-btn-primary px-4 py-2.5 md:px-5 md:py-2.5 text-[10px] font-bold tracking-widest flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                        onClick={() => sendMessage()}
                                        disabled={isLoading || isGuestLimitReached || (!input.trim() && !attachment)}
                                    >
                                        <span className="hidden sm:inline">TRANSMIT</span>
                                        <svg className="w-3 h-3 md:w-4 md:h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Legal Footer */}
                        {!isFocusMode && (
                            <p className="w-full text-center mt-3 mb-1 text-[8px] md:text-[9px] font-mono text-white/30 tracking-[0.2em] uppercase leading-relaxed">
                                MORATADEO IS NOT LIABLE FOR ANY OUTCOMES, DATA USAGE, OR ACTIONS CONDUCTED WITH MORATADEO'S AI.<br className="hidden md:block"/>
                                <Link to="/legal" className="hover:text-pink-400 transition-colors underline decoration-white/20 hover:decoration-pink-400/50 underline-offset-4 ml-2 max-md:mt-2 inline-block">TERMS OF SERVICE</Link>
                                <span className="inline-block mx-2 opacity-30">●</span>
                                <Link to="/legal" className="hover:text-pink-400 transition-colors underline decoration-white/20 hover:decoration-pink-400/50 underline-offset-4 inline-block">PRIVACY POLICY</Link>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ═══ Learn Mode — Bottom Slide-Up Drawer ═══ */}
            {isLearnMode && (
                <div className={`isho-learn-drawer ${isLearnDrawerOpen ? 'expanded' : 'collapsed'}`}>
                    {/* Handle Bar — always visible */}
                    <div className="isho-learn-handle" onClick={() => setIsLearnDrawerOpen(!isLearnDrawerOpen)}>
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="isho-signal-ring" />
                            <span className="font-mono text-[10px] font-bold text-pink-400 tracking-[0.2em] uppercase">LEARN MODE</span>
                            <span className="font-mono text-[10px] text-white/40 mx-1">—</span>
                            <span className="font-mono text-[10px] text-white/60 truncate max-w-[160px] md:max-w-[250px]">
                                {learnProgress ? learnProgress.topic.toUpperCase() : 'NO TOPIC'}
                            </span>
                            <div className="flex items-center gap-1 bg-pink-500/10 border border-pink-500/30 px-2 py-0.5 rounded-full text-pink-400 font-mono text-[9px] font-bold ml-2 shrink-0">
                                <span>🔥</span>
                                <span>{stats.streak_count || 0}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={(e) => { e.stopPropagation(); setIsLearnMode(false); }}
                                className="p-1 text-white/30 hover:text-white/60 transition-colors"
                                title="Exit Learn Mode"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                            <svg className="isho-drawer-chevron w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <polyline points="18 15 12 9 6 15" />
                            </svg>
                        </div>
                    </div>

                    {/* Drawer Tabs */}
                    <div className="isho-learn-tabs">
                        <button className={`isho-tab ${learnTab === 'focus' ? 'active' : ''}`} onClick={() => setLearnTab('focus')}>FOCUS</button>
                        <button className={`isho-tab ${learnTab === 'path' ? 'active' : ''}`} onClick={() => setLearnTab('path')}>PATH</button>
                        <button className={`isho-tab ${learnTab === 'stats' ? 'active' : ''}`} onClick={() => setLearnTab('stats')}>STATS</button>
                    </div>

                    {/* Drawer Body */}
                    <div className="isho-learn-body font-mono text-xs" data-lenis-prevent="true">
                        {learnTab === 'focus' && (
                            <>
                                {/* ── Card 1: Progress HUD + Phase Dots ── */}
                                <div className="isho-learn-card">
                                    <div className="flex items-center gap-4">
                                        {/* Circular Mastery Ring */}
                                        <div className="relative shrink-0">
                                            <svg width="64" height="64" viewBox="0 0 64 64" className="isho-mastery-ring">
                                                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                                                <circle
                                                    cx="32" cy="32" r="28"
                                                    fill="none"
                                                    stroke="url(#masteryGrad)"
                                                    strokeWidth="4"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${(learnProgress?.mastery_score || 0) / 100 * 175.93} 175.93`}
                                                    transform="rotate(-90 32 32)"
                                                />
                                                <defs>
                                                    <linearGradient id="masteryGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                        <stop offset="0%" stopColor="#a855f7" />
                                                        <stop offset="100%" stopColor="#f472b6" />
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="text-[11px] font-bold text-white">{learnProgress?.mastery_score || 0}%</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2 min-w-0">
                                            <span className="text-[10px] text-white/40 uppercase tracking-wider">PHASE {learnProgress?.phase || 1} OF 4</span>
                                            <div className="flex items-center gap-2">
                                                {[1, 2, 3, 4].map(p => (
                                                    <div
                                                        key={p}
                                                        className={`isho-phase-dot ${
                                                            p < (learnProgress?.phase || 1) ? 'completed' :
                                                            p === (learnProgress?.phase || 1) ? 'active' : ''
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-[9px] text-white/30 italic">
                                                {(learnProgress?.phase || 1) === 1 && "Foundations & Core Concepts"}
                                                {learnProgress?.phase === 2 && "Core Architecture & Components"}
                                                {learnProgress?.phase === 3 && "Advanced Optimization"}
                                                {learnProgress?.phase === 4 && "Scaling & Security"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Card 2: ELI5 Difficulty Segmented Control ── */}
                                <div className="isho-learn-card">
                                    <div className="flex flex-col gap-3">
                                        <div className="flex justify-between items-center text-[10px]">
                                            <span className="text-white/40 uppercase tracking-wider">LEVEL</span>
                                            <span className="text-pink-400 font-bold uppercase tracking-wider">{difficulty}</span>
                                        </div>
                                        <div className="isho-segmented-control">
                                            {['child', 'teen', 'adult', 'professional', 'expert'].map((lvl) => (
                                                <button
                                                    key={lvl}
                                                    onClick={() => {
                                                        setDifficulty(lvl);
                                                        localStorage.setItem('isho_difficulty', lvl);
                                                    }}
                                                    className={`segment ${difficulty === lvl ? 'active' : ''}`}
                                                >
                                                    {lvl === 'professional' ? 'PRO' : lvl.toUpperCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                        {/* ── Card 3: Active Quiz (full-width) ── */}
                        <div className="isho-learn-card full-width">
                            {activeQuiz ? (
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-2 text-[10px] font-bold text-pink-400 tracking-wider uppercase">
                                        {activeQuiz.type === 'analogy' && <span>🧩 ANALOGY PROMPT</span>}
                                        {activeQuiz.type === 'debate' && <span>⚔️ DEBATE CHALLENGE</span>}
                                        {activeQuiz.type === 'teach_back' && <span>🎓 TEACH-BACK</span>}
                                        {activeQuiz.type === 'scenario' && <span>⚠️ SCENARIO</span>}
                                        {activeQuiz.type === 'multiple_choice' && <span>❓ KNOWLEDGE CHECK</span>}
                                    </div>
                                    <p className="font-sans text-xs text-white/85 leading-relaxed">
                                        {activeQuiz.question}
                                    </p>
                                    {activeQuiz.type === 'multiple_choice' ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                                            {activeQuiz.options.map(opt => (
                                                <button
                                                    key={opt.key}
                                                    disabled={isLoading}
                                                    onClick={() => sendMessage(opt.key)}
                                                    className="w-full text-left p-2.5 rounded-lg border border-white/8 bg-white/[0.02] hover:border-pink-500/40 hover:bg-pink-500/5 transition-all text-[11px] font-sans text-white/65 hover:text-white flex items-center gap-2.5 group"
                                                >
                                                    <span className="w-5 h-5 rounded bg-white/8 group-hover:bg-pink-500 group-hover:text-black flex items-center justify-center font-mono font-bold text-[9px] transition-colors shrink-0">
                                                        {opt.key}
                                                    </span>
                                                    <span className="leading-snug">{opt.text}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2 mt-1">
                                            <textarea
                                                value={openEndedAnswer}
                                                onChange={(e) => setOpenEndedAnswer(e.target.value)}
                                                placeholder="Type your response..."
                                                className="w-full min-h-[60px] p-3 rounded-lg border border-white/8 bg-white/[0.02] focus:border-pink-500/40 focus:outline-none text-white font-sans text-xs resize-none"
                                                disabled={isLoading}
                                            />
                                            <button
                                                onClick={() => {
                                                    if (openEndedAnswer.trim()) {
                                                        sendMessage(openEndedAnswer.trim());
                                                        setOpenEndedAnswer('');
                                                    }
                                                }}
                                                disabled={isLoading || !openEndedAnswer.trim()}
                                                className="isho-btn isho-btn-primary w-full py-2 text-[9px] font-bold tracking-widest disabled:opacity-30"
                                            >
                                                SUBMIT RESPONSE
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center text-white/35 italic py-3 flex flex-col items-center gap-1.5">
                                    <span className="text-[11px]">No active question.</span>
                                    <span className="text-[9px] not-italic text-white/25">
                                        {learnProgress
                                            ? "Ask ISHO a question about this topic to trigger the next concept."
                                            : "Select a topic below to begin learning."}
                                    </span>
                                </div>
                            )}
                        </div>
                            </>
                        )}

                        {learnTab === 'path' && (
                            <>

                        {/* ── Card 4: Knowledge Map — Topic Grid ── */}
                        <div className="isho-learn-card full-width">
                            <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-3">KNOWLEDGE MAP</span>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {CURRICULUM_NODES.map(node => {
                                    const topicProgress = allTopicsProgress[normalizeTopicId(node.id)];
                                    const mastery = topicProgress?.mastery_score || 0;
                                    const isCurrent = learnProgress && normalizeTopicId(learnProgress.topic) === normalizeTopicId(node.id);
                                    return (
                                        <div
                                            key={node.id}
                                            onClick={() => startTopic(node)}
                                            className={`isho-topic-tile ${isCurrent ? 'active' : ''} ${mastery >= 100 ? 'mastered' : ''}`}
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <span className={`text-[9px] font-bold uppercase tracking-wider leading-tight ${isCurrent ? 'text-pink-400' : mastery > 0 ? 'text-white/70' : 'text-white/40'}`}>
                                                    {node.label}
                                                </span>
                                                {mastery > 0 && (
                                                    <span className={`text-[8px] font-bold ml-1 shrink-0 ${mastery >= 100 ? 'text-pink-400' : 'text-white/35'}`}>
                                                        {mastery}%
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mastery-fill" style={{ width: `${mastery}%` }} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                            </>
                        )}

                        {learnTab === 'stats' && (
                            <>

                        {/* ── Card 5: Achievements — Horizontal Pills ── */}
                        <div className="isho-learn-card full-width">
                            <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-3">ACHIEVEMENTS</span>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { id: 'socratic_rookie', label: 'Rookie', icon: '🌱' },
                                    { id: 'analogy_master', label: 'Analogy', icon: '🧩' },
                                    { id: 'debate_gladiator', label: 'Gladiator', icon: '⚔️' },
                                    { id: 'master_educator', label: 'Educator', icon: '🎓' },
                                    { id: 'crisis_solver', label: 'Crisis', icon: '⚠️' },
                                    { id: 'seven_day_streak', label: 'Weekly', icon: '🔥' },
                                    { id: 'topic_conqueror', label: 'Conqueror', icon: '👑' }
                                ].map(ach => {
                                    const isUnlocked = stats.unlocked_achievements?.includes(ach.id);
                                    return (
                                        <div
                                            key={ach.id}
                                            className={`isho-achievement-pill ${isUnlocked ? 'unlocked' : ''}`}
                                            title={ach.label}
                                        >
                                            <span className={isUnlocked ? '' : 'grayscale opacity-40'}>{ach.icon}</span>
                                            <span>{ach.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Card 6: Spaced Repetition Due Cards ── */}
                        <div className="isho-learn-card full-width">
                            <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-3">REVIEW SCHEDULE</span>
                            {Object.values(allTopicsProgress).length === 0 ? (
                                <p className="text-[10px] text-white/25 italic text-center py-2">No review tracks yet.</p>
                            ) : (
                                <div className="flex flex-col gap-1.5">
                                    {Object.values(allTopicsProgress)
                                        .map(tp => {
                                            const now = Date.now();
                                            const isDue = tp.dueDate ? tp.dueDate <= now : false;
                                            const daysLeft = tp.dueDate ? Math.ceil((tp.dueDate - now) / (1000 * 60 * 60 * 24)) : null;
                                            return { ...tp, isDue, daysLeft };
                                        })
                                        .sort((a, b) => {
                                            if (a.isDue && !b.isDue) return -1;
                                            if (!a.isDue && b.isDue) return 1;
                                            return (a.daysLeft || 0) - (b.daysLeft || 0);
                                        })
                                        .slice(0, 4)
                                        .map(tp => (
                                            <div key={tp.topic} className="flex justify-between items-center bg-white/[0.02] p-2 rounded-lg border border-white/5 text-[10px]">
                                                <span className="text-white/60 truncate max-w-[150px]">{tp.topic.toUpperCase()}</span>
                                                {tp.isDue ? (
                                                    <span className="isho-due-badge text-pink-400 bg-pink-500/10 border border-pink-500/20">DUE NOW</span>
                                                ) : (
                                                    <span className="text-white/35 text-[9px]">
                                                        {tp.daysLeft !== null
                                                            ? (tp.daysLeft <= 0 ? 'DUE TODAY' : `IN ${tp.daysLeft} DAYS`)
                                                            : 'NO DUE DATE'}
                                                    </span>
                                                )}
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                        </div>
                            </>
                        )}

                    </div>
                </div>
            )}

            {/* ═══ Guest Limit Auth Modal ═══ */}
            <AnimatePresence>
                {showAuthModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        {/* Overlay */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowAuthModal(false)}
                            className="absolute inset-0 bg-black/85 backdrop-blur-md cursor-pointer"
                        />
                        
                        {/* Modal Box */}
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                            className="relative w-full max-w-md p-8 rounded-3xl border border-pink-500/30 bg-black/90 shadow-[0_0_50px_rgba(244,114,182,0.15)] backdrop-blur-xl flex flex-col items-center text-center z-10"
                        >
                            {/* Glowing icon */}
                            <div className="w-16 h-16 rounded-full bg-pink-500/10 border border-pink-500/30 flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(244,114,182,0.1)]">
                                <svg className="w-8 h-8 text-pink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                </svg>
                            </div>
                            
                            <h2 className="font-mono text-sm md:text-base font-bold text-white tracking-[0.25em] uppercase mb-3">
                                GUEST ACCESS LIMIT EXHAUSTED
                            </h2>
                            <p className="font-sans text-xs text-white/50 leading-relaxed mb-6">
                                You have utilized all **7 of 7 free guest queries** for the ISHO AI system. Please sign in to unlock unlimited high-performance queries, persistent socratic learn tracks, and session logging.
                            </p>
                            
                            <div className="flex flex-col gap-3 w-full">
                                <button 
                                    disabled
                                    className="isho-btn isho-btn-primary w-full py-3 text-xs font-mono font-bold tracking-widest text-center cursor-not-allowed opacity-50"
                                >
                                    LOGIN IS DISABLED
                                </button>
                                <button 
                                    onClick={() => setShowAuthModal(false)}
                                    className="isho-btn w-full py-3 text-xs font-mono tracking-widest text-white/40 hover:text-white/60 transition-colors cursor-pointer"
                                >
                                    CLOSE WINDOW
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </main>
    );
}
